import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { auth, storage, db, functions } from './firebase';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, getDocs, query, where, orderBy, limit, addDoc, updateDoc, deleteDoc, doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';

import { httpsCallable } from 'firebase/functions';
import Success from './Success';
import AdminDashboard from './AdminDashboard';
import { PayButton, BookingPaymentBlock } from './PaymentButton';
import EditablePageContent from './EditablePageContent';
import EditableMultiSectionContent from './EditableMultiSectionContent';
import EditableStructuredPageContent from './EditableStructuredPageContent';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';
import UserProfile, { getFirstName, getPublicName } from './UserProfile';
import { ProfileLink, ProfileRow, ProfileChip, HostCard, OwnerStoryCard, MessageUserButton, fetchUserProfile } from './ProfileLink';
import { CommunityPulse, LocalExpertStrip, SearchEmptyWantedPrompt, fetchCommunityStats } from './CommunitySections';
import { HangarListingChecklist } from './HangarChecklist';
import { migratePrivateFieldsFromPublic, savePrivateProfile } from './userPrivateProfile';
import {
  DEFAULT_SITE_CONTENT,
  MULTI_SECTION_PAGE_IDS,
  STRUCTURED_PAGE_IDS,
  isCodeManagedPage,
  normalizeStructuredSections,
  SITE_OWNER_EMAIL,
  SITE_PAGE_IDS,
  normalizePageContent,
  normalizePageSections,
} from './siteContent';
import {
  calculateRentalDays,
  calculateRentalTotal,
  capConditionGuaranteeAmount,
  canOwnerCancelRental,
  canOwnerReleaseConditionHold,
  canRenterCancelRental,
  getCancelRefundMessage,
  getConditionHoldAmount,
  isOutsidePayoutWindow,
  isRentalCancelled,
  getOwnerPayoutSummary,
  getRentalPaymentAmount,
  formatPayoutDueLabel,
  formatChargeDueLabel,
  formatRentalStatus,
  filterActiveBookedDates,
  isRentalPayable,
  isRentalPaid,
  isRentalPaymentScheduled,
  selectRenterPaymentRental,
  MAX_CONDITION_GUARANTEE,
  GEAR_IMAGE_PLACEHOLDER,
  OWNER_SHARE_PERCENT,
  PLATFORM_FEE_PERCENT,
} from './rentalPayments';
import {
  GearRentalHistory,
  ReviewModal,
} from './ReviewComponents';
import {
  formatDatesInMessageText,
  formatMessageTimestamp,
  formatRentalDate,
  formatRentalDateRange,
  parseLocalDate,
} from './dateUtils';
import { compressImage } from './imageCompression';
import ConditionReportCard from './ConditionReportCard';
import {
  formatCityState,
  parseTypedLocation,
  resolveLocationInput,
} from './locationResolver';
import { isPublicRentalHistoryEntry, isRentalReviewEligible, hasUserReviewedRental } from './reviewUtils';
import { useUnreadMessages } from './useUnreadMessages';
import { usePaymentDueCount } from './usePaymentDueCount';
import { usePendingRentalCount } from './usePendingRentalCount';
import { useIsGearOwner } from './useIsGearOwner';
import GearShareButtons from './GearShareButtons';
import HomeHero, {
  BenefitTiles,
  HowItWorks,
  OwnerListCTA,
  PopularCategories,
  TrustStrip,
} from './HomeHero';
import FoundingListerBanner from './FoundingListerBanner';
import {
  GearWantedPostForm,
  GearWantedResultsSection,
  HomeDiscoverySection,
  MyGearWantedSection,
} from './GearWanted';
import { geocodeLocation, getDistanceInMiles } from './geoUtils';
import { filterWantedByProximity, isWantedPostActive } from './gearWantedUtils';
import {
  getCategoryDisplayName,
  isVehicleDisclaimerCategory,
  isVehicleDisclaimerGear,
  VehicleCategoryDisclaimerBanner,
  VEHICLE_DISCLAIMER_VERSION,
} from './VehicleCategoryDisclaimer';
import {
  attachReviewSummaries,
  fetchReviewSummariesForGear,
  formatGearLocationLabel,
  formatLocationDisplay,
} from './gearReviewSummaries';
import {
  GearInsuranceFaqLink,
  GearPayThroughCallout,
  GearReviewsPreview,
} from './GearTrustCallout';
import FaqPage from './FaqPage';
import { renderStarRating } from './reviewUtils';
import { useToast } from './Toast';
import AnalyticsRouteListener from './AnalyticsRouteListener';
import PageMeta, { buildGearProductSchema, buildHomePageSchema } from './PageMeta';
import StripeConnectBanner from './StripeConnectBanner';
import { useStripeConnectBanner } from './useStripeConnectBanner';
import { DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE } from './siteConfig';
import {
  buildConversationList,
  findActiveConversationInList,
  getThreadMessages,
  markConversationEngaged,
  touchMessagingEngagement,
} from './messagingState';

const MIN_GEAR_DESCRIPTION_LENGTH = 80;

function getGearDescriptionValidationError(description) {
  const trimmed = (description || '').trim();
  if (trimmed.length < MIN_GEAR_DESCRIPTION_LENGTH) {
    return `Please write a unique description of at least ${MIN_GEAR_DESCRIPTION_LENGTH} characters. Mention condition, what is included, and what makes this gear great for renters.`;
  }
  return null;
}


function getGearLocationText(gear) {
  if (!gear?.location) return '';
  if (typeof gear.location === 'string') return gear.location.toLowerCase();
  return [gear.location.city, gear.location.state, gear.location.zip]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function getGearSearchText(gear, categories) {
  const categoryName = categories.find(cat => cat.id === gear.category)?.name || gear.category || '';
  return [
    gear.name,
    gear.description,
    categoryName,
    getGearLocationText(gear),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function matchesKeyword(gear, keyword, categories) {
  const trimmed = keyword?.trim();
  if (!trimmed) return true;
  const tokens = trimmed.toLowerCase().split(/\s+/).filter(Boolean);
  const searchText = getGearSearchText(gear, categories);
  return tokens.every(token => searchText.includes(token));
}

function matchesLocationText(gear, locationQuery) {
  const query = locationQuery?.trim().toLowerCase();
  if (!query) return true;
  const gearLocation = getGearLocationText(gear);
  if (!gearLocation) return false;
  if (gearLocation.includes(query)) return true;
  return query
    .split(/[,\s]+/)
    .filter(part => part.length > 1)
    .some(part => gearLocation.includes(part));
}

function matchesProximity(gear, searchLat, searchLon, maxMiles, locationQuery) {
  if (gear.coordinates?.latitude && gear.coordinates?.longitude && searchLat != null && searchLon != null) {
    const dist = getDistanceInMiles(searchLat, searchLon, gear.coordinates.latitude, gear.coordinates.longitude);
    return dist <= maxMiles;
  }
  return matchesLocationText(gear, locationQuery);
}

function filterGearByAvailability(gearList, startDate, endDate) {
  if (!startDate || !endDate) return gearList;
  const reqStart = new Date(startDate);
  const reqEnd = new Date(endDate);
  return gearList.filter(gear =>
    gear.dateRanges?.some(range =>
      new Date(range.startDate) <= reqEnd &&
      new Date(range.endDate) >= reqStart
    ) &&
    (!gear.bookedDates?.length || !filterActiveBookedDates(gear.bookedDates).some(booked => {
      const bookedStart = new Date(booked.startDate);
      const bookedEnd = new Date(booked.endDate);
      return reqStart <= bookedEnd && reqEnd >= bookedStart;
    }))
  );
}
// Reusable modern card style (used by all components)
const cardStyle = {
  padding: '24px',
  border: '1px solid #e5e7eb',
  borderRadius: '16px',
  backgroundColor: '#ffffff',
  boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
  transition: 'all 0.3s ease'
};
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) return <h1>Something went wrong: {this.state.error?.message || 'Unknown error'}</h1>;
    return this.props.children;
  }
}

function Section({ title, children }) {
  return (
    <div className="section" id={title.toLowerCase().replace(/\s+/g, '-')}>
      <h2>{title}</h2>
      {children}
    </div>
  );
}

function getStaticPageDescription(pageKey, sectionTexts) {
  if (STRUCTURED_PAGE_IDS.includes(pageKey)) {
    const sections = sectionTexts[pageKey];
    if (Array.isArray(sections) && sections[0]?.paragraphs?.[0]) {
      return sections[0].paragraphs[0];
    }
    return DEFAULT_DESCRIPTION;
  }

  if (MULTI_SECTION_PAGE_IDS.includes(pageKey)) {
    return sectionTexts[pageKey]?.[0] || DEFAULT_DESCRIPTION;
  }

  return sectionTexts[pageKey] || DEFAULT_DESCRIPTION;
}

function StaticContentPage({ title, pageKey, sectionTexts, canEditSite, isContentLoading, onTextChange }) {
  const isStructured = STRUCTURED_PAGE_IDS.includes(pageKey);
  const isMultiSection = MULTI_SECTION_PAGE_IDS.includes(pageKey);
  const pageDescription = getStaticPageDescription(pageKey, sectionTexts);
  // Legal/policy pages are code-managed so their history is in git, not
  // overwritten in place. See CODE_MANAGED_PAGE_IDS.
  const canEdit = canEditSite && !isCodeManagedPage(pageKey);

  return (
    <>
      <PageMeta title={title} description={pageDescription} path={`/${pageKey}`} />
    <div className={`static-content-page${isStructured ? ' static-content-page-structured' : ''}`}>
    <Section title={title}>
      {isStructured ? (
        <EditableStructuredPageContent
          sections={sectionTexts[pageKey]}
          isAdmin={canEdit}
          isLoading={isContentLoading}
          onSave={(sections) => onTextChange(pageKey, sections)}
        />
      ) : isMultiSection ? (
        <EditableMultiSectionContent
          sections={sectionTexts[pageKey]}
          isAdmin={canEdit}
          isLoading={isContentLoading}
          onSave={(sections) => onTextChange(pageKey, sections)}
        />
      ) : (
        <EditablePageContent
          content={sectionTexts[pageKey]}
          isAdmin={canEdit}
          isLoading={isContentLoading}
          onSave={(text) => onTextChange(pageKey, text)}
        />
      )}
    </Section>
    </div>
    </>
  );
}
// ==================== GEAR SEARCH ====================
function GearSearch({
  onSearchResults,
  categories,
  categoryPreset = '',
  onCategoryPresetApplied,
  compactTitle = false,
}) {
  const { showToast } = useToast();
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [proximity, setProximity] = useState('Nationwide');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [keyword, setKeyword] = useState('');
  const [isFormValid, setIsFormValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isNationwide = proximity === 'Nationwide';

  useEffect(() => {
    // Nationwide searches never require a location; local radius searches do.
    setIsFormValid(isNationwide || location.trim() !== '');
  }, [location, isNationwide]);

  useEffect(() => {
    if (!categoryPreset) return;
    setCategory(categoryPreset);
    onCategoryPresetApplied?.();
  }, [categoryPreset, onCategoryPresetApplied]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const trimmedLocation = location.trim();
    const trimmedKeyword = keyword.trim();

    if (!isNationwide && !trimmedLocation) {
      showToast('Please enter a location to search within your selected radius.', 'error');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let searchLat = null;
      let searchLon = null;

      if (!isNationwide && trimmedLocation) {
        const coords = await geocodeLocation(trimmedLocation);
        if (!coords) {
          setError('Could not find that location. Try a city, state, or zip code.');
          return;
        }
        searchLat = coords.lat;
        searchLon = coords.lon;
      }

      const snapshot = await getDocs(collection(db, 'gear'));
      let filtered = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      if (category) {
        filtered = filtered.filter(gear => gear.category === category);
      }

      if (trimmedKeyword) {
        filtered = filtered.filter(gear => matchesKeyword(gear, trimmedKeyword, categories));
      }

      filtered = filterGearByAvailability(filtered, startDate, endDate);

      if (!isNationwide && trimmedLocation) {
        const maxMiles = parseInt(proximity, 10);
        filtered = filtered.filter(gear =>
          matchesProximity(gear, searchLat, searchLon, maxMiles, trimmedLocation)
        );
      } else if (isNationwide && trimmedLocation && !trimmedKeyword) {
        filtered = filtered.filter(gear => matchesLocationText(gear, trimmedLocation));
      }

      onSearchResults(filtered, trimmedLocation, proximity, trimmedKeyword);
    } catch (err) {
      console.error(err);
      setError('Search failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div id="gear-search" className="home-search-section">
      {!compactTitle && (
        <h3 style={{ marginBottom: '20px', color: '#1f2937', fontSize: '1.5rem' }}>Find Your Next Adventure</h3>
      )}
      <form onSubmit={handleSearch} className="gear-search-form" style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
        <select value={category} onChange={e => setCategory(e.target.value)} style={{ padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1rem' }}>
          <option value="">All Categories</option>
          {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
        </select>

        <input 
          type="text" 
          placeholder="What are you looking for? (e.g. kayak, tent, bike)"
          value={keyword} 
          onChange={e => setKeyword(e.target.value)} 
          style={{ padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1rem' }}
        />

        <input
          type="text"
          placeholder={isNationwide ? 'Location (optional)' : 'Location (city, state, or zip code)'}
          value={location}
          onChange={e => setLocation(e.target.value)}
          style={{ padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1rem' }}
        />

        <select value={proximity} onChange={e => setProximity(e.target.value)} style={{ padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1rem' }}>
          <option value="10">Within 10 miles</option>
          <option value="50">Within 50 miles</option>
          <option value="100">Within 100 miles</option>
          <option value="Nationwide">Nationwide</option>
        </select>

        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1rem', fontFamily: 'inherit' }} />
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1rem', fontFamily: 'inherit' }} />

        <button 
          type="submit" 
          disabled={!isFormValid || isLoading}
          style={{
            padding: '14px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontWeight: '600',
            fontSize: '1.05rem',
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
        >
          {isLoading ? 'Searching...' : 'Search for your trip'}
        </button>
      </form>
      {error && <p style={{ color: '#ef4444', marginTop: '12px' }}>{error}</p>}
    </div>
  );
}

// ==================== IMPROVED GEAR ITEM ====================
function GearItem({ gear, user, categories = [] }) {
  const navigate = useNavigate();
  const handleViewDetails = () => navigate(`/gear/${gear.id}`);
  const categoryLabel =
    categories.find((cat) => cat.id === gear.category)?.name || gear.category || '—';
  const locationLabel = formatGearLocationLabel(gear.location);
  const reviewSummary = gear.reviewSummary;

  return (
    <div 
      className="gear-item gear-item-card"
      onClick={handleViewDetails}
      style={{
        display: 'flex',
        gap: '24px',
        padding: '24px',
        border: '1px solid #e5e7eb',
        borderRadius: '16px',
        backgroundColor: '#ffffff',
        boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginBottom: '28px'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-6px)';
        e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.07)';
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <img 
          src={gear.mainPicture || GEAR_IMAGE_PLACEHOLDER} 
          alt={gear.name}
          style={{
            width: '180px',
            height: '180px',
            objectFit: 'cover',
            borderRadius: '12px'
          }}
          
        />
      </div>

      <div style={{ flex: 1 }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>{gear.name}</h4>

        {reviewSummary?.count > 0 ? (
          <p className="gear-item-rating">
            {renderStarRating(reviewSummary.rating)}
            <span className="gear-item-rating-meta">
              {reviewSummary.rating.toFixed(1)} · {reviewSummary.count}{' '}
              {reviewSummary.count === 1 ? 'review' : 'reviews'}
            </span>
          </p>
        ) : (
          <p className="gear-item-no-reviews">No renter reviews yet</p>
        )}
        
        <p style={{ margin: '8px 0', color: '#4b5563', fontSize: '1rem' }}>
          <strong>Category:</strong> {categoryLabel}
        </p>
        <p style={{ margin: '8px 0', color: '#4b5563', fontSize: '1rem' }}>
          <strong>Location:</strong> {locationLabel}
        </p>
        <p style={{ margin: '8px 0', color: '#4b5563', fontSize: '1rem' }}>
          <strong>Price:</strong> ${gear.pricePerDay || '—'} / day
        </p>

        {gear.ownerId && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ margin: '8px 0', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}
          >
            <ProfileChip userId={gear.ownerId} label="Hosted by" />
            <MessageUserButton
              user={user}
              recipientId={gear.ownerId}
              gearId={gear.id}
              gearName={gear.name}
              label="Message Owner"
              compact
            />
          </div>
        )}

        <p style={{
          margin: '16px 0 0 0', 
          color: '#374151',
          lineHeight: '1.5',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {gear.description}
        </p>

        <button 
          onClick={(e) => { e.stopPropagation(); handleViewDetails(); }}
          style={{
            marginTop: '20px',
            padding: '12px 28px',
            backgroundColor: '#3b82f6',
            color: '#ffffff',
            border: 'none',
            borderRadius: '10px',
            fontWeight: '600',
            fontSize: '1.02rem',
            cursor: 'pointer'
          }}
        >
          View Details
        </button>
      </div>
    </div>
  );
}

// GEAR_IMAGE_PLACEHOLDER imported from rentalPayments

function getGearPrimaryImage(gear) {
  if (!gear) return null;
  return gear.mainPicture || gear.images?.[0] || gear.additionalPictures?.[0] || null;
}

// ==================== GEAR DETAIL PAGE - FULL FEATURES + BLOCKED DATES ====================
function GearSubProfile({ user, onOpenLogin, categories = [] }) {
  const { showToast } = useToast();
  const { gearId } = useParams();
  const [searchParams] = useSearchParams();
  const rentalIdParam = searchParams.get('rental');
  const [gear, setGear] = useState(null);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [scheduledRentals, setScheduledRentals] = useState([]);
  const [rentalHistory, setRentalHistory] = useState([]);
  const [bookedDates, setBookedDates] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [inquiryTitle, setInquiryTitle] = useState('');
  const [inquiryComment, setInquiryComment] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [linkedRental, setLinkedRental] = useState(null);
  const [requestDisclaimerAcknowledged, setRequestDisclaimerAcknowledged] = useState(false);
  const [paymentDisclaimerAcknowledged, setPaymentDisclaimerAcknowledged] = useState(false);

  useEffect(() => {
    if (!rentalIdParam || !user?.uid || !gearId) {
      setLinkedRental(null);
      return;
    }

    const loadLinkedRental = async () => {
      try {
        const rentalSnap = await getDoc(doc(db, 'rentals', rentalIdParam));
        if (!rentalSnap.exists()) {
          setLinkedRental(null);
          return;
        }

        const rental = { id: rentalSnap.id, ...rentalSnap.data() };
        if (rental.renterId === user.uid && rental.gearId === gearId) {
          setLinkedRental(rental);
        } else {
          setLinkedRental(null);
        }
      } catch (error) {
        console.error('Error loading linked rental:', error);
        setLinkedRental(null);
      }
    };

    loadLinkedRental();
  }, [rentalIdParam, user, gearId]);

  useEffect(() => {
    setRequestDisclaimerAcknowledged(false);
    setPaymentDisclaimerAcknowledged(false);
  }, [gearId]);

  useEffect(() => {
    if (!gearId) {
      setLoading(false);
      return;
    }

    const fetchGear = async () => {
      try {
        const gearDoc = await getDoc(doc(db, 'gear', gearId));
        if (gearDoc.exists()) {
          const gearData = { id: gearDoc.id, ...gearDoc.data() };
          setGear(gearData);
          setImageLoadFailed(false);
          setBookedDates(gearData.bookedDates || []);

          let allRentals = [];
          if (user?.uid) {
            const rentalDocs = new Map();
            const renterRentalsQuery = query(
              collection(db, 'rentals'),
              where('gearId', '==', gearId),
              where('renterId', '==', user.uid)
            );
            const renterRentalsSnap = await getDocs(renterRentalsQuery);
            renterRentalsSnap.docs.forEach((rentalDoc) => {
              rentalDocs.set(rentalDoc.id, { id: rentalDoc.id, ...rentalDoc.data() });
            });

            if (gearData.ownerId === user.uid) {
              const ownerRentalsQuery = query(
                collection(db, 'rentals'),
                where('gearId', '==', gearId),
                where('ownerId', '==', user.uid)
              );
              const ownerRentalsSnap = await getDocs(ownerRentalsQuery);
              ownerRentalsSnap.docs.forEach((rentalDoc) => {
                rentalDocs.set(rentalDoc.id, { id: rentalDoc.id, ...rentalDoc.data() });
              });
            }

            allRentals = Array.from(rentalDocs.values());
          }

          setScheduledRentals(allRentals.filter((rental) => rental.status === 'approved'));
          setRentalHistory(
            allRentals
              .filter(isPublicRentalHistoryEntry)
              .sort((a, b) => new Date(b.rentalEndDate) - new Date(a.rentalEndDate))
          );

          const reviewsQuery = query(collection(db, 'reviews'), where('gearId', '==', gearId));
          const reviewsSnap = await getDocs(reviewsQuery);
          const gearReviews = reviewsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setReviews(
            gearReviews.filter(
              (review) => review.type === 'renter-review' || !review.type
            )
          );
        }
      } catch (error) {
        console.error('Error fetching gear:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGear();
  }, [gearId, user]);

  useEffect(() => {
    if (loading || !gear) return;

    const renterRental =
      user && user.uid !== gear.ownerId
        ? linkedRental || selectRenterPaymentRental(scheduledRentals, user.uid, rentalIdParam)
        : null;

    if (window.location.hash === '#rental-payment' && renterRental) {
      requestAnimationFrame(() => {
        document.getElementById('rental-payment')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [loading, gear, scheduledRentals, linkedRental, user, rentalIdParam]);

  if (loading) return <p style={{ textAlign: 'center', padding: '80px' }}>Loading gear details...</p>;
  if (!gear) return <p style={{ textAlign: 'center', padding: '80px' }}>Gear not found.</p>;

  const userApprovedRental =
    user && user.uid !== gear.ownerId
      ? linkedRental || selectRenterPaymentRental(scheduledRentals, user.uid, rentalIdParam)
      : null;

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length).toFixed(1) 
    : 0;

  const activeBookedDates = filterActiveBookedDates(bookedDates);
  const requiresVehicleDisclaimer = isVehicleDisclaimerGear(gear, categories);
  const gearCategoryName = getCategoryDisplayName(gear.category, categories);
  const rentalDatesSelected = Boolean(startDate && endDate);
  const requestDisclaimerRequired =
    requiresVehicleDisclaimer && rentalDatesSelected && !requestDisclaimerAcknowledged;

  const handleRentalStartDateChange = (value) => {
    setStartDate(value);
    setRequestDisclaimerAcknowledged(false);
  };

  const handleRentalEndDateChange = (value) => {
    setEndDate(value);
    setRequestDisclaimerAcknowledged(false);
  };

  // Check for overlapping dates
  const hasOverlap = (selStart, selEnd) => {
    if (!selStart || !selEnd) return false;
    const reqStart = parseLocalDate(selStart);
    const reqEnd = parseLocalDate(selEnd);
    return activeBookedDates.some(booked => {
      const bStart = parseLocalDate(booked.startDate);
      const bEnd = parseLocalDate(booked.endDate);
      return reqStart <= bEnd && reqEnd >= bStart;
    });
  };

  const handleRentalRequest = async () => {
    if (!user) {
      showToast('Please log in or create an account to request this gear.', 'info');
      onOpenLogin?.();
      return;
    }
    if (!startDate || !endDate) {
      showToast('Please select both a start and end date.', 'error');
      return;
    }
    if (honeypot) return;
    if (hasOverlap(startDate, endDate)) {
      showToast('Selected dates overlap with booked dates. Please choose different dates.', 'error');
      return;
    }
    if (requiresVehicleDisclaimer && !requestDisclaimerAcknowledged) {
      showToast('Please read and acknowledge the rental notice before requesting this gear.', 'error');
      return;
    }

    try {
      const rentalData = {
        renterId: user.uid,
        ownerId: gear.ownerId,
        gearId: gear.id,
        gearName: gear.name,
        rentalStartDate: startDate,
        rentalEndDate: endDate,
        comments: 'Rental request via detail page',
        status: 'pending',
        createdAt: new Date().toISOString(),
        participants: [user.uid, gear.ownerId]
      };

      const rentalRef = await addDoc(collection(db, 'rentals'), rentalData);

      const convId = `conv-${rentalRef.id}`;
      await addDoc(collection(db, 'messages'), {
        conversationId: convId,
        senderId: user.uid,
        recipientId: gear.ownerId,
        gearId: gear.id,
        gearName: gear.name,
        text: `New rental request for ${gear.name} from ${formatRentalDateRange(startDate, endDate)}`,
        timestamp: new Date().toISOString(),
        participants: [user.uid, gear.ownerId]
      });

      showToast('Rental request sent. Check Messaging for updates.', 'success');
    } catch (error) {
      showToast(`Error sending rental request: ${error.message}`, 'error');
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      showToast('Please log in or create an account to send an inquiry.', 'info');
      onOpenLogin?.();
      return;
    }
    if (!inquiryTitle || !inquiryComment) {
      showToast('Please fill in all inquiry fields.', 'error');
      return;
    }
    if (honeypot) return;

    try {
      const convId = `conv-inq-${gear.id}-${Date.now()}`;
      await addDoc(collection(db, 'messages'), {
        conversationId: convId,
        senderId: user.uid,
        recipientId: gear.ownerId,
        gearId: gear.id,
        gearName: gear.name,
        title: inquiryTitle,
        text: inquiryComment,
        timestamp: new Date().toISOString(),
        participants: [user.uid, gear.ownerId]
      });

      showToast('Inquiry sent. Check Messaging for the conversation.', 'success');
      setShowInquiryForm(false);
      setInquiryTitle('');
      setInquiryComment('');
    } catch (error) {
      showToast(`Error sending inquiry: ${error.message}`, 'error');
    }
  };

    const productSchema = buildGearProductSchema(gear, reviews);

    return (
    <>
      <PageMeta
        title={gear.name}
        description={gear.description}
        path={`/gear/${gear.id}`}
        image={getGearPrimaryImage(gear) || DEFAULT_OG_IMAGE}
        type="product"
        jsonLd={productSchema}
      />
    <div style={{ maxWidth: '1100px', margin: '40px auto', padding: '0 24px' }}>
      <div className="gear-detail-layout" style={{ display: 'flex', gap: '48px', flexWrap: 'wrap' }}>
        {/* Left Column - Image & Info */}
        <div style={{ flex: '1 1 580px' }}>
          <div className="gear-detail-media">
            <img
              src={imageLoadFailed ? GEAR_IMAGE_PLACEHOLDER : (getGearPrimaryImage(gear) || GEAR_IMAGE_PLACEHOLDER)}
              alt={gear.name}
              className="gear-detail-image"
              onError={() => setImageLoadFailed(true)}
            />
            <GearShareButtons gearName={gear.name} gearId={gear.id} />
          </div>
          <h1 style={{ fontSize: '2.2rem', marginBottom: '12px' }}>{gear.name}</h1>
          <div style={{ fontSize: '1.35rem', color: '#f59e0b', marginBottom: '16px' }}>
            {reviews.length > 0 ? (
              <>
                {renderStarRating(averageRating)}
                <span style={{ marginLeft: '10px', color: '#6b7280', fontSize: '1rem' }}>
                  {averageRating} · {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
                </span>
              </>
            ) : (
              <span style={{ color: '#9ca3af', fontSize: '1rem' }}>No renter reviews yet</span>
            )}
          </div>
          {gear.ownerId && (
            <OwnerStoryCard
              userId={gear.ownerId}
              user={user}
              gearId={gear.id}
              gearName={gear.name}
              listingNote={gear.ownerDescription}
            />
          )}
          <GearReviewsPreview reviews={reviews} />
          <p style={{ fontSize: '1.6rem', fontWeight: '600', marginBottom: '16px' }}>${gear.pricePerDay}/day</p>
          <p style={{ margin: '0 0 20px', lineHeight: '1.7' }}>{gear.description}</p>

        </div>

        {/* Right Column - Booking + Blocked Dates */}
        <div style={{ flex: '1 1 420px' }}>
          <h3>Request This Gear</h3>

          <GearPayThroughCallout />
          <GearInsuranceFaqLink gear={gear} categories={categories} />

          {!user && (
            <div className="gear-guest-cta">
              <strong>Sign in to request this gear.</strong>
              <div>Create a free account or log in to choose dates and send a rental request.</div>
              <div className="gear-guest-cta-actions">
                <button type="button" className="gear-guest-cta-login" onClick={onOpenLogin}>
                  Log in
                </button>
                <Link to="/create-profile" className="gear-guest-cta-signup">
                  Create account
                </Link>
              </div>
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <input type="date" value={startDate} onChange={(e) => handleRentalStartDateChange(e.target.value)} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db' }} />
            <input type="date" value={endDate} onChange={(e) => handleRentalEndDateChange(e.target.value)} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db' }} />
          </div>

          {requiresVehicleDisclaimer && rentalDatesSelected && (
            <div style={{ marginBottom: '16px' }}>
              <VehicleCategoryDisclaimerBanner
                variant="renter"
                categoryName={gearCategoryName}
                acknowledged={requestDisclaimerAcknowledged}
                onAcknowledgedChange={setRequestDisclaimerAcknowledged}
              />
            </div>
          )}

          <input type="text" name="honeypot" value={honeypot} onChange={e => setHoneypot(e.target.value)} style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />

          <button 
            onClick={handleRentalRequest} 
            disabled={!user || !startDate || !endDate || hasOverlap(startDate, endDate) || requestDisclaimerRequired} 
            style={{ width: '100%', padding: '16px', backgroundColor: !user || hasOverlap(startDate, endDate) || requestDisclaimerRequired ? '#9ca3af' : '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600' }}
          >
            {!user
              ? 'Log in to request this rental'
              : hasOverlap(startDate, endDate)
                ? 'Dates Unavailable - Choose Different Dates'
                : requestDisclaimerRequired
                  ? 'Acknowledge notice to continue'
                  : 'Request This Rental'}
          </button>

          <RentalPaymentSection
            user={user}
            gear={gear}
            rental={userApprovedRental}
            categories={categories}
            vehicleDisclaimerAcknowledged={paymentDisclaimerAcknowledged}
            onVehicleDisclaimerAcknowledgedChange={setPaymentDisclaimerAcknowledged}
          />

          {/* Unavailable Dates Section */}
          <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#fef2f2', borderRadius: '12px' }}>
            <h4 style={{ color: '#ef4444', marginBottom: '12px' }}>📅 Unavailable Dates</h4>
            {activeBookedDates.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {activeBookedDates.map((b, i) => (
                  <div key={i} style={{ padding: '10px 16px', backgroundColor: '#fee2e2', borderRadius: '8px', border: '1px solid #f87171' }}>
                    {formatRentalDateRange(b.startDate, b.endDate)}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#6b7280' }}>No dates blocked yet.</p>
            )}
          </div>

          <button onClick={() => setShowInquiryForm(true)} style={{ marginTop: '24px', width: '100%', padding: '14px', backgroundColor: 'white', color: '#3b82f6', border: '2px solid #3b82f6', borderRadius: '10px', fontWeight: '600' }}>
            Send an Inquiry
          </button>
        </div>
      </div>

      <div id="rental-history" style={{ marginTop: '60px' }}>
        <h3 style={{ marginBottom: '8px', color: '#1f2937' }}>Rental History</h3>
        <p style={{ margin: '0 0 20px 0', color: '#6b7280', fontSize: '0.95rem' }}>
          Past rentals and renter feedback for this item.
        </p>
        <GearRentalHistory
          rentals={rentalHistory}
          reviews={reviews}
          gearName={gear.name}
          currentUserId={user?.uid}
        />
      </div>

      {/* Inquiry Modal */}
      {showInquiryForm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
          <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '16px', width: '90%', maxWidth: '520px' }}>
            <h3>Send Gear Inquiry</h3>
            {gear.ownerId && <HostCard userId={gear.ownerId} compact />}
            <form onSubmit={handleInquirySubmit}>
              <input type="text" placeholder="Subject" value={inquiryTitle} onChange={e => setInquiryTitle(e.target.value)} required style={{ width: '100%', padding: '14px', marginBottom: '16px', borderRadius: '10px', border: '1px solid #d1d5db' }} />
              <textarea placeholder="Your message..." value={inquiryComment} onChange={e => setInquiryComment(e.target.value)} required style={{ width: '100%', height: '140px', padding: '14px', marginBottom: '20px', borderRadius: '10px', border: '1px solid #d1d5db' }} />

              <input type="text" name="honeypot" value={honeypot} onChange={e => setHoneypot(e.target.value)} style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button type="submit" style={{ flex: 1, padding: '14px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600' }}>Send Inquiry</button>
                <button type="button" onClick={() => setShowInquiryForm(false)} style={{ flex: 1, padding: '14px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '10px' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

// ==================== STANDALONE CONTACT PAGE ====================
function ContactPage() {
  const { showToast } = useToast();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setIsFormValid(firstName && lastName && email && phone && message.length > 5 && message.length <= 1000);
  }, [firstName, lastName, email, phone, message]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsSubmitting(true);

    try {
      const submitContactForm = httpsCallable(functions, 'submitContactForm');
      await submitContactForm({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        message: message.trim(),
      });

      setSuccess(true);
      setFirstName(''); setLastName(''); setEmail(''); setPhone(''); setMessage('');
    } catch (error) {
      showToast('Error: ' + error.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <>
        <PageMeta title="Contact Us" description="Thanks for contacting ATTACK! Your Adventure." path="/contact" />
      <div style={{ maxWidth: '600px', margin: '80px auto', textAlign: 'center', padding: '40px' }}>
        <h2 style={{ color: '#10b981' }}>Thank You!</h2>
        <p style={{ fontSize: '1.2rem', marginTop: '16px' }}>Your message has been received. We'll get back to you soon.</p>
        <Link to="/" style={{ marginTop: '32px', display: 'inline-block', padding: '12px 32px', backgroundColor: '#3b82f6', color: 'white', textDecoration: 'none', borderRadius: '10px' }}>
          Return to Home
        </Link>
      </div>
      </>
    );
  }

  return (
    <>
      <PageMeta
        title="Contact Us"
        description="Have questions or feedback about gear rentals? Contact ATTACK! Your Adventure."
        path="/contact"
      />
    <div style={{ maxWidth: '700px', margin: '60px auto', padding: '0 24px' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '12px', color: '#1f2937' }}>Contact Us</h1>
      <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '48px', fontSize: '1.1rem' }}>
        Have questions or feedback? We'd love to hear from you.
      </p>

      <form onSubmit={handleSubmit} style={{ backgroundColor: '#ffffff', padding: '48px', borderRadius: '16px', boxShadow: '0 4px 25px rgba(0,0,0,0.08)' }}>
        <div className="contact-name-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
          <input type="text" placeholder="First Name" value={firstName} onChange={e => setFirstName(e.target.value)} required style={{ padding: '16px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1.05rem' }} />
          <input type="text" placeholder="Last Name" value={lastName} onChange={e => setLastName(e.target.value)} required style={{ padding: '16px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1.05rem' }} />
        </div>

        <input type="email" placeholder="Email Address" value={email} onChange={e => setEmail(e.target.value)} required style={{ width: '100%', padding: '16px', marginBottom: '20px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1.05rem' }} />

        <input type="tel" placeholder="Phone Number" value={phone} onChange={e => setPhone(e.target.value)} required style={{ width: '100%', padding: '16px', marginBottom: '24px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1.05rem' }} />

        <textarea placeholder="Your message (max 1000 characters)" value={message} onChange={e => setMessage(e.target.value)} maxLength={1000} required style={{ width: '100%', height: '160px', padding: '16px', marginBottom: '24px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1.05rem', resize: 'vertical' }} />

        <button type="submit" disabled={!isFormValid || isSubmitting} style={{ width: '100%', padding: '16px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '1.1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
    </>
  );
}

// ==================== PENDING RENTALS (with Approve/Deny + Edit Dates) ====================
function PendingRentals({ user }) {
  const { showToast } = useToast();
  const [pendingRentals, setPendingRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { needsStripeConnect } = useStripeConnectBanner(user);

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRental, setEditingRental] = useState(null);
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');

  useEffect(() => {
    if (!user) return;

    const fetchPending = async () => {
      try {
        const q = query(
          collection(db, 'rentals'),
          where('ownerId', '==', user.uid),
          where('status', '==', 'pending')
        );
        const snap = await getDocs(q);
        setPendingRentals(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error('Error fetching pending rentals:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPending();
  }, [user]);

  // Open Edit Modal
  const openEditModal = (rental) => {
    setEditingRental(rental);
    setNewStartDate(rental.rentalStartDate);
    setNewEndDate(rental.rentalEndDate);
    setShowEditModal(true);
  };

  // Save Edited Dates
  const handleUpdateRentalDates = async () => {
    if (!editingRental || !newStartDate || !newEndDate) {
      showToast('Please select both dates', 'error');
      return;
    }

    try {
      await updateDoc(doc(db, 'rentals', editingRental.id), {
        rentalStartDate: newStartDate,
        rentalEndDate: newEndDate,
        updatedAt: new Date().toISOString()
      });

      showToast('Rental dates updated successfully!', 'success');
      setShowEditModal(false);
      window.location.reload(); // Refresh list
    } catch (error) {
      console.error("Error updating dates:", error);
      showToast('Failed to update: ' + error.message, 'error');
    }
  };

  // Approve or Deny Request
    // Approve or Deny Request + Automatic Date Blocking
  const respondToRequest = async (rentalId, status, ownerComment = '') => {
    try {
      // Update rental status
      await updateDoc(doc(db, 'rentals', rentalId), {
        status: status,
        ownerComment: ownerComment || '',
        respondedAt: new Date().toISOString()
      });

      if (status === 'approved') {
        const rentalSnap = await getDoc(doc(db, 'rentals', rentalId));
        const rental = rentalSnap.data();

        // Block these dates on the gear
        const gearRef = doc(db, 'gear', rental.gearId);
        const gearSnap = await getDoc(gearRef);
        const gear = gearSnap.data() || {};
        const totalAmount = calculateRentalTotal(
          gear.pricePerDay,
          rental.rentalStartDate,
          rental.rentalEndDate
        );

        await updateDoc(doc(db, 'rentals', rentalId), {
          paymentStatus: 'unpaid',
          paymentNotificationSeen: false,
          pricePerDay: gear.pricePerDay || null,
          totalAmount,
          conditionGuaranteeAmount: capConditionGuaranteeAmount(gear.conditionGuaranteeAmount),
        });

        const newBookedDate = {
          startDate: rental.rentalStartDate,
          endDate: rental.rentalEndDate,
          rentalId: rentalId,
          renterId: rental.renterId
        };

        const currentBooked = gear.bookedDates || [];
        await updateDoc(gearRef, {
          bookedDates: [...currentBooked, newBookedDate]
        });

        showToast('Rental approved and dates automatically blocked!', 'success');
      } else {
        showToast('Rental request denied.', 'info');
      }

      window.location.reload(); // Refresh lists
    } catch (error) {
      console.error('Error responding to request:', error);
      showToast('Error: ' + error.message, 'error');
    }
  };

  if (!user) return <p style={{ textAlign: 'center', padding: '80px' }}>Please log in.</p>;

  return (
    <>
      <PageMeta
        title="Pending Rentals"
        description="Review and respond to incoming gear rental requests."
        path="/pending-rentals"
      />
    <div style={{ maxWidth: '1100px', margin: '40px auto', padding: '0 24px' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '40px' }}>Pending Rental Requests</h2>
      {needsStripeConnect && <StripeConnectBanner />}

      {loading ? (
        <p style={{ textAlign: 'center' }}>Loading...</p>
      ) : pendingRentals.length > 0 ? (
        pendingRentals.map((r) => (
          <div key={r.id} style={{ ...cardStyle, marginBottom: '24px' }}>
            <h4>
              {r.gearId ? (
                <Link to={`/gear/${r.gearId}`} style={{ color: '#1f2937', textDecoration: 'none' }}>
                  {r.gearName || 'Gear Request'}
                </Link>
              ) : (
                r.gearName || 'Gear Request'
              )}
            </h4>
            <ProfileRow label="Renter" userId={r.renterId} />
            <p><strong>Dates:</strong> {formatRentalDateRange(r.rentalStartDate, r.rentalEndDate)}</p>
            {r.comments && <p><strong>Message:</strong> "{r.comments}"</p>}

            <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button 
                onClick={() => respondToRequest(r.id, 'approved')}
                style={{ padding: '10px 20px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              >
                Approve
              </button>

              <button 
                onClick={() => respondToRequest(r.id, 'denied')}
                style={{ padding: '10px 20px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              >
                Deny
              </button>

              <button 
                onClick={() => openEditModal(r)}
                style={{ padding: '10px 20px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              >
                Edit Dates
              </button>
            </div>
          </div>
        ))
      ) : (
        <p style={{ textAlign: 'center', color: '#6b7280' }}>No pending rental requests.</p>
      )}

      {/* Edit Rental Modal */}
      {showEditModal && editingRental && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
          <div className="modal-panel" style={{ backgroundColor: 'white', padding: '32px', borderRadius: '16px', width: '90%', maxWidth: '480px' }}>
            <h3>Edit Rental Dates</h3>
            <p><strong>Gear:</strong> {editingRental.gearName || 'Gear'}</p>
            <ProfileRow label="Renter" userId={editingRental.renterId} />

            <label>Start Date</label>
            <input 
              type="date" 
              value={newStartDate} 
              onChange={(e) => setNewStartDate(e.target.value)} 
              style={{ width: '100%', padding: '10px', margin: '8px 0 16px' }}
            />

            <label>End Date</label>
            <input 
              type="date" 
              value={newEndDate} 
              onChange={(e) => setNewEndDate(e.target.value)} 
              style={{ width: '100%', padding: '10px', margin: '8px 0 24px' }}
            />

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button 
                onClick={handleUpdateRentalDates}
                style={{ flex: 1, padding: '12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              >
                Save Changes
              </button>
              <button 
                onClick={() => setShowEditModal(false)}
                style={{ flex: 1, padding: '12px', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

// ==================== APP COMPONENT ====================
function App() {
  const [user, setUser] = useState(null);
  const { showToast } = useToast();
  const { unreadCount, unreadConversationIds } = useUnreadMessages(user);
  const { paymentDueCount, paymentDueRentalIds } = usePaymentDueCount(user);
  const { pendingRentalCount } = usePendingRentalCount(user);
  const isGearOwner = useIsGearOwner(user);
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [categories, setCategories] = useState([]);
  const [sectionTexts, setSectionTexts] = useState({ ...DEFAULT_SITE_CONTENT });
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEnablingAdmin, setIsEnablingAdmin] = useState(false);
  const [isContentLoading, setIsContentLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [otherItems, setOtherItems] = useState([]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(setUser);

    const fetchCategories = async () => {
      const snap = await getDocs(collection(db, 'categories'));
      setCategories(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchCategories();

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadSiteContent = async () => {
      setIsContentLoading(true);
      try {
        const texts = { ...DEFAULT_SITE_CONTENT };
        await Promise.all(
          SITE_PAGE_IDS.map(async (pageId) => {
            const pageSnap = await getDoc(doc(db, 'pages', pageId));
            if (pageSnap.exists()) {
              const pageData = pageSnap.data();
              texts[pageId] = normalizePageContent(pageId, pageData.content, pageData.sections);
            }
          })
        );
        setSectionTexts(texts);
      } catch (error) {
        console.error('Error loading site content:', error);
      } finally {
        setIsContentLoading(false);
      }
    };

    loadSiteContent();
  }, []);

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      setUserProfile(null);
      return;
    }

    const loadUserData = async () => {
      try {
        const [adminSnap, profileSnap] = await Promise.all([
          getDoc(doc(db, 'admins', user.uid)),
          getDoc(doc(db, 'users', user.uid)),
        ]);
        setIsAdmin(adminSnap.exists());
        if (profileSnap.exists()) {
          const migratedProfile = await migratePrivateFieldsFromPublic(user.uid, profileSnap.data());
          setUserProfile(migratedProfile);
        } else {
          setUserProfile(null);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        setIsAdmin(false);
        setUserProfile(null);
      }
    };

    loadUserData();
  }, [user]);

  const handleSignIn = (e) => {
    e.preventDefault();
    if (email && password) {
      signInWithEmailAndPassword(auth, email, password)
        .then(() => setShowModal(false))
        .catch((err) => showToast(err.message, 'error'));
    }
  };

  const handleSignOut = () => signOut(auth).then(() => setUser(null)).catch((err) => showToast(err.message, 'error'));

  const handleEnableSiteEditing = async () => {
    if (!user) {
      showToast('Please log in first.', 'info');
      return;
    }

    setIsEnablingAdmin(true);
    try {
      const bootstrapAdmin = httpsCallable(functions, 'bootstrapAdmin');
      await bootstrapAdmin();
      setIsAdmin(true);
      showToast('Admin edit mode enabled.', 'success');
    } catch (error) {
      const message = error?.message || 'Could not enable site editing.';
      showToast(message, 'error');
    } finally {
      setIsEnablingAdmin(false);
    }
  };

  const isSiteOwner = user?.email?.toLowerCase() === SITE_OWNER_EMAIL;
  const canEditSite = isSiteOwner && isAdmin;
  const headerFirstName = user
    ? getFirstName(userProfile, user.displayName || 'Profile')
    : '';

  const handleTextChange = async (pageId, newContent) => {
    const normalizedContent = STRUCTURED_PAGE_IDS.includes(pageId)
      ? normalizeStructuredSections(newContent)
      : MULTI_SECTION_PAGE_IDS.includes(pageId)
        ? normalizePageSections(newContent)
        : newContent;

    setSectionTexts((prev) => ({ ...prev, [pageId]: normalizedContent }));

    if (!canEditSite || !user) return;
    // Policy pages ship with the code so their history lives in git; the
    // database rules reject these writes anyway.
    if (isCodeManagedPage(pageId)) return;

    try {
      const usesSectionsPayload = STRUCTURED_PAGE_IDS.includes(pageId) || MULTI_SECTION_PAGE_IDS.includes(pageId);
      const payload = usesSectionsPayload
        ? {
            sections: normalizedContent,
            updatedAt: new Date().toISOString(),
            updatedBy: user.uid,
          }
        : {
            content: normalizedContent,
            updatedAt: new Date().toISOString(),
            updatedBy: user.uid,
          };

      await setDoc(doc(db, 'pages', pageId), payload, { merge: true });
    } catch (error) {
      console.error('Error saving site content:', error);
      showToast(`Could not save page content: ${error.message}`, 'error');
    }
  };

  const handleSearchResults = async (gear, location, proximity, keyword = '') => {
    let enrichedResults = gear;
    try {
      const summaries = await fetchReviewSummariesForGear(gear.map((item) => item.id));
      enrichedResults = attachReviewSummaries(gear, summaries);
    } catch (error) {
      console.error('Error loading review summaries for search results:', error);
    }
    setSearchResults(enrichedResults);
    const trimmedKeyword = keyword?.trim() || '';
    const trimmedLocation = location?.trim() || '';
    const isNationwide = proximity === 'Nationwide';

    // "Other items in the area" only applies to local keyword searches.
    if (!trimmedLocation || isNationwide || !trimmedKeyword) {
      setOtherItems([]);
      return;
    }

    try {
      const coords = await geocodeLocation(trimmedLocation);
      if (!coords) {
        setOtherItems([]);
        return;
      }

      const maxMiles = parseInt(proximity, 10);
      const allSnap = await getDocs(collection(db, 'gear'));
      const allGear = allSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const matchedIds = new Set(enrichedResults.map(item => item.id));

      const otherGear = allGear.filter(item => {
        if (matchedIds.has(item.id)) return false;
        return matchesProximity(item, coords.lat, coords.lon, maxMiles, trimmedLocation);
      });

      try {
        const otherSummaries = await fetchReviewSummariesForGear(otherGear.map((item) => item.id));
        setOtherItems(attachReviewSummaries(otherGear, otherSummaries));
      } catch (error) {
        console.error('Error loading review summaries for other items:', error);
        setOtherItems(otherGear);
      }
    } catch (err) {
      console.error(err);
      setOtherItems([]);
    }
  };

  return (
    <Router>
      <AnalyticsRouteListener />
      <ErrorBoundary>
        <div className="App" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
          <SiteHeader
            user={user}
            isGearOwner={isGearOwner}
            headerFirstName={headerFirstName}
            canEditSite={canEditSite}
            isSiteOwner={isSiteOwner}
            isAdmin={isAdmin}
            isEnablingAdmin={isEnablingAdmin}
            messagingUnreadCount={unreadCount}
            paymentDueCount={paymentDueCount}
            pendingRentalCount={pendingRentalCount}
            onEnableSiteEditing={handleEnableSiteEditing}
            onSignOut={handleSignOut}
            onOpenLogin={() => setShowModal(true)}
          />

          <div className="main-content-offset">
            {showModal && !user && (
              <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                <div className="login-modal-content" style={{ backgroundColor: 'white', padding: '40px', borderRadius: '16px', width: '400px' }}>
                  <h2>Login</h2>
                  <form onSubmit={handleSignIn}>
                    <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required style={{ width: '100%', padding: '14px', marginBottom: '12px', borderRadius: '10px', border: '1px solid #d1d5db' }} />
                    <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required style={{ width: '100%', padding: '14px', marginBottom: '24px', borderRadius: '10px', border: '1px solid #d1d5db' }} />
                    <button type="submit" style={{ width: '100%', padding: '14px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600' }}>Login</button>
                  </form>
                  <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <Link to="/create-profile" onClick={() => setShowModal(false)} style={{ color: '#3b82f6' }}>Create Account</Link> | 
                    <Link to="/forgot-password" onClick={() => setShowModal(false)} style={{ color: '#3b82f6', marginLeft: '12px' }}>Forgot Password?</Link>
                  </div>
                </div>
              </div>
            )}
            <div className="page-container" style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 32px' }} key={window.location.pathname}>
              <Routes>
                <Route path="/" element={<HomePage user={user} searchResults={searchResults} otherItems={otherItems} onSearchResults={handleSearchResults} categories={categories} sectionTexts={sectionTexts} canEditSite={canEditSite} isContentLoading={isContentLoading} onTextChange={handleTextChange} onOpenLogin={() => setShowModal(true)} />} />
                <Route path="/browse" element={<HomePage browseMode user={user} searchResults={searchResults} otherItems={otherItems} onSearchResults={handleSearchResults} categories={categories} sectionTexts={sectionTexts} canEditSite={canEditSite} isContentLoading={isContentLoading} onTextChange={handleTextChange} onOpenLogin={() => setShowModal(true)} />} />
                <Route path="/about" element={<StaticContentPage title="About Us" pageKey="about" sectionTexts={sectionTexts} canEditSite={canEditSite} isContentLoading={isContentLoading} onTextChange={handleTextChange} />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/rules" element={<StaticContentPage title="Rules of Engagement" pageKey="rules" sectionTexts={sectionTexts} canEditSite={canEditSite} isContentLoading={isContentLoading} onTextChange={handleTextChange} />} />
                <Route path="/terms" element={<StaticContentPage title="Terms of Service" pageKey="terms" sectionTexts={sectionTexts} canEditSite={canEditSite} isContentLoading={isContentLoading} onTextChange={handleTextChange} />} />
                <Route path="/privacy" element={<StaticContentPage title="Privacy Policy" pageKey="privacy" sectionTexts={sectionTexts} canEditSite={canEditSite} isContentLoading={isContentLoading} onTextChange={handleTextChange} />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/create-profile" element={<CreateProfile />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/profile" element={<UserProfile currentUser={user} onProfileUpdate={setUserProfile} />} />
                <Route path="/profile/:userId" element={<UserProfile currentUser={user} onProfileUpdate={setUserProfile} />} />
                <Route path="/your-hangar" element={<YourHangar user={user} categories={categories} />} />
                <Route path="/messaging" element={<Messaging user={user} unreadConversationIds={unreadConversationIds} />} />
                <Route path="/pending-rentals" element={<PendingRentals user={user} />} />
                <Route path="/gear/:gearId" element={<GearSubProfile user={user} categories={categories} onOpenLogin={() => setShowModal(true)} />} />
                <Route path="/my-bookings" element={<MyBookings user={user} paymentDueRentalIds={paymentDueRentalIds} />} />
                <Route path="/scheduled-gear" element={<ScheduledGear user={user} />} />
                <Route path="/find" element={<GearSubProfile user={user} categories={categories} />} />
                <Route path="/success" element={<Success />} />
                <Route path="/admin" element={<AdminDashboard user={user} isAdmin={isAdmin} />} />
              </Routes>
              <SiteFooter user={user} />
            </div>
          </div>
        </div>
      </ErrorBoundary>
    </Router>
  );
}

function RentalPaymentSection({
  user,
  gear,
  rental,
  categories = [],
  vehicleDisclaimerAcknowledged = false,
  onVehicleDisclaimerAcknowledgedChange,
}) {
  if (!rental || !gear || !user) return null;
  if (user.uid !== rental.renterId || user.uid === gear.ownerId) return null;

  const amount = getRentalPaymentAmount(rental, gear);
  const conditionHoldAmount = getConditionHoldAmount(gear, rental);
  const requiresVehicleDisclaimer = isVehicleDisclaimerGear(gear, categories);
  const gearCategoryName = getCategoryDisplayName(gear.category, categories);
  const paymentDisclaimerRequired =
    requiresVehicleDisclaimer && !vehicleDisclaimerAcknowledged;

  if (isRentalPaymentScheduled(rental)) {
    const chargeLabel = formatChargeDueLabel(rental);
    return (
      <div
        id="rental-payment"
        style={{
          marginTop: '12px',
          padding: '16px',
          borderRadius: '10px',
          backgroundColor: '#fffbeb',
          border: '1px solid #fcd34d',
        }}
      >
        <p style={{ margin: 0, color: '#92400e', fontWeight: '700', fontSize: '1rem', textAlign: 'center' }}>
          Card on File
        </p>
        <p style={{ margin: '6px 0 0 0', color: '#b45309', fontSize: '0.92rem', textAlign: 'center', lineHeight: '1.5' }}>
          Your card is saved. You will be charged ${amount.toFixed(2)}
          {chargeLabel ? ` on ${chargeLabel}` : ' 48 hours before your rental starts'}.
        </p>
        <p style={{ margin: '8px 0 0 0', color: '#92400e', fontSize: '0.88rem', textAlign: 'center' }}>
          Cancel before then and your card will not be charged.
        </p>
      </div>
    );
  }

  if (isRentalPaid(rental)) {
    const paidDate = rental.paidAt ? formatRentalDate(rental.paidAt) : null;
    const payoutLabel = formatPayoutDueLabel(rental);
    const holdStatus = rental.conditionHoldStatus;

    return (
      <div
        id="rental-payment"
        style={{
          marginTop: '12px',
          padding: '16px',
          borderRadius: '10px',
          backgroundColor: '#ecfdf5',
          border: '1px solid #a7f3d0',
        }}
      >
        <p style={{ margin: 0, color: '#047857', fontWeight: '700', fontSize: '1rem', textAlign: 'center' }}>
          Payment Complete
        </p>
        <p style={{ margin: '6px 0 0 0', color: '#059669', fontSize: '0.92rem', textAlign: 'center' }}>
          {paidDate ? `Paid on ${paidDate}` : 'This rental has been paid.'}
        </p>
        {payoutLabel && (
          <p style={{ margin: '10px 0 0 0', color: '#065f46', fontSize: '0.88rem', lineHeight: '1.5' }}>
            The gear owner receives their share on or around <strong>{payoutLabel}</strong> (48 hours before your rental starts).
          </p>
        )}
        {conditionHoldAmount > 0 && holdStatus === 'authorized' && (
          <p style={{ margin: '8px 0 0 0', color: '#065f46', fontSize: '0.88rem', lineHeight: '1.5' }}>
            A <strong>${conditionHoldAmount.toFixed(2)}</strong> condition guarantee hold is on your card. The owner will release it once the gear is returned in acceptable condition.
          </p>
        )}
        {conditionHoldAmount > 0 && holdStatus === 'released' && (
          <p style={{ margin: '8px 0 0 0', color: '#065f46', fontSize: '0.88rem' }}>
            Your condition guarantee hold has been released.
          </p>
        )}
      </div>
    );
  }

  if (!isRentalPayable(rental)) {
    return (
      <div
        id="rental-payment"
        style={{
          marginTop: '12px',
          padding: '14px 16px',
          borderRadius: '10px',
          backgroundColor: '#fff7ed',
          border: '1px solid #fed7aa',
          color: '#9a3412',
          fontSize: '0.92rem',
        }}
      >
        Payment is no longer available for this rental.
      </div>
    );
  }

  if (amount < 1) {
    return (
      <div
        id="rental-payment"
        style={{
          marginTop: '12px',
          padding: '14px 16px',
          borderRadius: '10px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#991b1b',
          fontSize: '0.92rem',
        }}
      >
        We could not calculate a payment total for this rental. Please message the owner or contact support.
      </div>
    );
  }

  return (
    <div id="rental-payment">
      <div
        style={{
          marginTop: '12px',
          padding: '14px 16px',
          borderRadius: '10px',
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          fontSize: '0.9rem',
          color: '#475569',
          lineHeight: '1.55',
        }}
      >
        <p style={{ margin: '0 0 8px 0', fontWeight: '700', color: '#1e293b' }}>How payment works</p>
        <p style={{ margin: 0 }}>
          Save your card to secure this booking. Your card will be charged 48 hours before your rental starts (or immediately if you book inside that window).
          ATTACK! Your Adventure keeps {PLATFORM_FEE_PERCENT}% and sends {OWNER_SHARE_PERCENT}% to the gear owner at that time.
        </p>
        {conditionHoldAmount > 0 && (
          <p style={{ margin: '8px 0 0 0' }}>
            After the charge, a separate <strong>${conditionHoldAmount.toFixed(2)}</strong> authorization (up to ${MAX_CONDITION_GUARANTEE.toLocaleString()}) may be placed on your card.
            It is not charged unless gear is returned in unacceptable condition. The owner releases it after return.
          </p>
        )}
      </div>
      {requiresVehicleDisclaimer && (
        <div style={{ marginTop: '12px' }}>
          <VehicleCategoryDisclaimerBanner
            variant="renter"
            categoryName={gearCategoryName}
            acknowledged={vehicleDisclaimerAcknowledged}
            onAcknowledgedChange={onVehicleDisclaimerAcknowledgedChange}
          />
        </div>
      )}
      <PayButton
        amount={amount}
        gearName={gear.name}
        rentalData={{
          gearId: gear.id,
          rentalId: rental.id,
          startDate: rental.rentalStartDate,
          endDate: rental.rentalEndDate,
        }}
        disabled={paymentDisclaimerRequired}
      />
    </div>
  );
}

function CancelRentalButton({ rental, role, onCancelled }) {
  const { showToast } = useToast();
  const [loading, setLoading] = React.useState(false);
  const canCancel =
    role === 'owner'
      ? canOwnerCancelRental(rental, rental.viewerId)
      : canRenterCancelRental(rental, rental.viewerId);

  if (!canCancel) return null;

  const refundMessage = getCancelRefundMessage(rental, role);
  const paid = isRentalPaid(rental);
  const outsideWindow = isOutsidePayoutWindow(rental);

  const handleCancel = async () => {
    if (
      !window.confirm(
        `Cancel this rental?\n\n${refundMessage}\n\nThis cannot be undone.`
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const cancelRental = httpsCallable(functions, 'cancelRental');
      const result = await cancelRental({ rentalId: rental.id });
      const data = result.data || {};

      if (data.refundStatus === 'refunded_full') {
        showToast('Rental cancelled. A full refund has been issued.', 'success');
      } else if (data.refundStatus === 'not_refunded') {
        showToast(
          'Rental cancelled. Because this was within 48 hours of the start date, no refund was issued and the dates are open again.',
          'info'
        );
      } else if (data.refundStatus === 'manual_required') {
        showToast(
          'Rental cancelled. Our team will review the refund on this booking and follow up with you.',
          'info'
        );
      } else {
        showToast('Rental cancelled successfully.', 'success');
      }

      onCancelled?.();
    } catch (error) {
      showToast(error.message || 'Could not cancel rental.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        marginTop: '12px',
        padding: '14px 16px',
        borderRadius: '10px',
        backgroundColor: outsideWindow || !paid ? '#fef2f2' : '#fff7ed',
        border: `1px solid ${outsideWindow || !paid ? '#fecaca' : '#fed7aa'}`,
      }}
    >
      <p style={{ margin: '0 0 10px 0', color: '#7c2d12', fontSize: '0.92rem', lineHeight: '1.5' }}>
        {refundMessage}
      </p>
      <button
        type="button"
        onClick={handleCancel}
        disabled={loading}
        style={{
          padding: '10px 18px',
          backgroundColor: loading ? '#9ca3af' : '#dc2626',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontWeight: '700',
          cursor: loading ? 'not-allowed' : 'pointer',
        }}
      >
        {loading ? 'Cancelling...' : 'Cancel rental'}
      </button>
    </div>
  );
}

function OwnerReleaseHoldButton({ rental, onReleased }) {
  const { showToast } = useToast();
  const [loading, setLoading] = React.useState(false);
  const holdAmount = getConditionHoldAmount(null, rental);

  if (!canOwnerReleaseConditionHold(rental, rental.viewerId)) {
    if (rental.conditionHoldStatus === 'released') {
      return (
        <p style={{ margin: '10px 0 0 0', color: '#047857', fontWeight: '600', fontSize: '0.92rem' }}>
          Condition hold released — renter was not charged.
        </p>
      );
    }
    return null;
  }

  const handleRelease = async () => {
    if (
      !window.confirm(
        `Release the $${holdAmount.toFixed(2)} condition hold? Only do this if the gear was returned in acceptable condition.`
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const releaseHold = httpsCallable(functions, 'releaseRentalConditionHold');
      await releaseHold({ rentalId: rental.id });
      showToast('Condition hold released.', 'success');
      onReleased?.();
    } catch (error) {
      showToast(error.message || 'Could not release condition hold.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        marginTop: '12px',
        padding: '14px 16px',
        borderRadius: '10px',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
      }}
    >
      <p style={{ margin: '0 0 10px 0', color: '#1e3a8a', fontSize: '0.92rem', lineHeight: '1.5' }}>
        A <strong>${holdAmount.toFixed(2)}</strong> condition guarantee hold is active on the renter&apos;s card.
        Release it once you have the gear back in good condition.
      </p>
      <button
        type="button"
        onClick={handleRelease}
        disabled={loading}
        style={{
          padding: '10px 18px',
          backgroundColor: loading ? '#9ca3af' : '#059669',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontWeight: '700',
          cursor: loading ? 'not-allowed' : 'pointer',
        }}
      >
        {loading ? 'Releasing...' : 'Release condition hold'}
      </button>
    </div>
  );
}

// ==================== MY BOOKINGS - TWO SECTIONS + EDIT RENTAL + REVIEWS ====================
const BOOKINGS_PAGE_SIZE = 5;

function sortBookingsByStartDate(rentals) {
  return [...rentals].sort(
    (a, b) => parseLocalDate(b.rentalStartDate) - parseLocalDate(a.rentalStartDate)
  );
}

function BookingsPagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', margin: '16px 0 28px' }}>
      <button
        type="button"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        style={{
          padding: '8px 14px',
          borderRadius: '8px',
          border: '1px solid #d1d5db',
          background: page <= 1 ? '#f3f4f6' : '#ffffff',
          cursor: page <= 1 ? 'not-allowed' : 'pointer',
        }}
      >
        Previous
      </button>
      <span style={{ color: '#6b7280', fontSize: '0.92rem' }}>
        Page {page} of {totalPages}
      </span>
      <button
        type="button"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        style={{
          padding: '8px 14px',
          borderRadius: '8px',
          border: '1px solid #d1d5db',
          background: page >= totalPages ? '#f3f4f6' : '#ffffff',
          cursor: page >= totalPages ? 'not-allowed' : 'pointer',
        }}
      >
        Next
      </button>
    </div>
  );
}

function MyBookings({ user, paymentDueRentalIds = new Set() }) {
  const { showToast } = useToast();
  const [myRentals, setMyRentals] = useState([]);           // Things I rented as renter
  const [myScheduledGear, setMyScheduledGear] = useState([]); // Things I'm renting out as owner
  const [loading, setLoading] = useState(true);
  const [rentalsPage, setRentalsPage] = useState(1);
  const [scheduledPage, setScheduledPage] = useState(1);

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRental, setEditingRental] = useState(null);
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');

  // Review modal states
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewRental, setReviewRental] = useState(null);
  const [reviewAsOwner, setReviewAsOwner] = useState(false);
  const [myReviews, setMyReviews] = useState([]);

  const fetchBookings = useCallback(async () => {
    if (!user) return;

    try {
      const renterQuery = query(collection(db, 'rentals'), where('renterId', '==', user.uid));
      const renterSnap = await getDocs(renterQuery);
      setMyRentals(renterSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const ownerQuery = query(collection(db, 'rentals'), where('ownerId', '==', user.uid));
      const ownerSnap = await getDocs(ownerQuery);
      setMyScheduledGear(ownerSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const reviewsQuery = query(collection(db, 'reviews'), where('reviewerId', '==', user.uid));
      const reviewsSnap = await getDocs(reviewsQuery);
      setMyReviews(reviewsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const openEditModal = (rental) => {
    setEditingRental(rental);
    setNewStartDate(rental.rentalStartDate);
    setNewEndDate(rental.rentalEndDate);
    setShowEditModal(true);
  };

  const openReviewModal = (rental, asOwner = false) => {
    setReviewRental(rental);
    setReviewAsOwner(asOwner);
    setShowReviewModal(true);
  };

  const handleUpdateRentalDates = async () => {
    if (!editingRental || !newStartDate || !newEndDate) {
      showToast('Please select both dates', 'error');
      return;
    }

    try {
      await updateDoc(doc(db, 'rentals', editingRental.id), {
        rentalStartDate: newStartDate,
        rentalEndDate: newEndDate,
        updatedAt: new Date().toISOString()
      });

      showToast('Rental dates updated successfully!', 'success');
      setShowEditModal(false);
      window.location.reload();
    } catch (error) {
      console.error("Error updating rental:", error);
      showToast('Failed to update: ' + error.message, 'error');
    }
  };

  if (!user) return <p style={{ textAlign: 'center', padding: '80px' }}>Please log in to view your bookings.</p>;

  const sortedRentals = sortBookingsByStartDate(myRentals);
  const sortedScheduledGear = sortBookingsByStartDate(myScheduledGear);
  const rentalsTotalPages = Math.max(1, Math.ceil(sortedRentals.length / BOOKINGS_PAGE_SIZE));
  const scheduledTotalPages = Math.max(1, Math.ceil(sortedScheduledGear.length / BOOKINGS_PAGE_SIZE));
  const currentRentalsPage = Math.min(rentalsPage, rentalsTotalPages);
  const currentScheduledPage = Math.min(scheduledPage, scheduledTotalPages);
  const pagedRentals = sortedRentals.slice(
    (currentRentalsPage - 1) * BOOKINGS_PAGE_SIZE,
    currentRentalsPage * BOOKINGS_PAGE_SIZE
  );
  const pagedScheduledGear = sortedScheduledGear.slice(
    (currentScheduledPage - 1) * BOOKINGS_PAGE_SIZE,
    currentScheduledPage * BOOKINGS_PAGE_SIZE
  );

  return (
    <>
      <PageMeta
        title="My Bookings"
        description="View your rentals, payment status, and scheduled gear on ATTACK! Your Adventure."
        path="/my-bookings"
      />
    <div style={{ maxWidth: '1100px', margin: '40px auto', padding: '0 24px' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '40px' }}>My Bookings</h2>

      {loading ? (
        <p style={{ textAlign: 'center' }}>Loading...</p>
      ) : (
        <>
          <h3 className="bookings-section-title" style={{ color: '#3b82f6' }}>Rentals</h3>
          <p className="bookings-section-subtitle">Gear you've booked from other members.</p>
          {sortedRentals.length > 0 ? (
            pagedRentals.map((b) => {
              const paymentDue = paymentDueRentalIds.has(b.id);
              return (
              <div
                key={b.id}
                className={paymentDue ? 'booking-payment-due' : ''}
                style={{ ...cardStyle, marginBottom: '20px' }}
              >
                <h4 style={{ fontWeight: paymentDue ? '700' : '600' }}>
                  {paymentDue && <span style={{ color: '#d97706', marginRight: '8px' }}>● Payment due</span>}
                  {b.gearId ? (
                    <Link to={`/gear/${b.gearId}`} style={{ color: '#1f2937', textDecoration: 'none' }}>
                      {b.gearName || 'Gear'}
                    </Link>
                  ) : (
                    b.gearName || 'Gear'
                  )}
                </h4>
                <ProfileRow label="Owner" userId={b.ownerId} />
                <p><strong>Dates:</strong> {formatRentalDateRange(b.rentalStartDate, b.rentalEndDate)}</p>
                <p><strong>Status:</strong> {formatRentalStatus(b.status)}</p>
                {isRentalCancelled(b) && (
                  <p style={{ marginTop: '8px', color: '#b45309', fontSize: '0.92rem', lineHeight: '1.5' }}>
                    {b.refundStatus === 'refunded_full'
                      ? 'Cancelled — full refund issued.'
                      : b.refundStatus === 'not_charged'
                        ? 'Cancelled — your card was not charged.'
                      : b.refundStatus === 'manual_required'
                        ? 'Cancelled — refund to be arranged with the owner.'
                        : 'This rental was cancelled.'}
                  </p>
                )}
                {b.status === 'approved' && (
                  <p>
                    <strong>Payment:</strong>{' '}
                    {isRentalPaid(b) ? (
                      <span style={{ color: '#10b981', fontWeight: '600' }}>Paid</span>
                    ) : isRentalPaymentScheduled(b) ? (
                      <span style={{ color: '#d97706', fontWeight: '600' }}>Card on file — charge scheduled</span>
                    ) : (
                      <span style={{ color: '#f59e0b', fontWeight: '600' }}>Awaiting payment setup</span>
                    )}
                  </p>
                )}
                {b.ownerComment && <p><strong>Owner Note:</strong> "{b.ownerComment}"</p>}

                <CancelRentalButton
                  rental={{ ...b, viewerId: user.uid }}
                  role="renter"
                  onCancelled={fetchBookings}
                />

                <BookingPaymentBlock rental={b} />

                <ConditionReportCard rental={b} userId={user.uid} />

                {b.status === 'approved' && !isRentalPaid(b) && !isRentalPayable(b) && !isRentalCancelled(b) && (
                  <p style={{ marginTop: '12px', color: '#b45309', fontWeight: '600', fontSize: '0.92rem' }}>
                    Payment is no longer available — these rental dates have passed.
                  </p>
                )}

                {b.status === 'approved' && isRentalPaid(b) && (
                  <div style={{ marginTop: '12px', color: '#047857', fontSize: '0.92rem', lineHeight: '1.5' }}>
                    <p style={{ margin: 0, fontWeight: '600' }}>
                      Payment received — you&apos;re all set for your rental.
                    </p>
                    {formatPayoutDueLabel(b) && (
                      <p style={{ margin: '6px 0 0 0' }}>
                        Owner payout scheduled around {formatPayoutDueLabel(b)}.
                      </p>
                    )}
                    {getConditionHoldAmount(null, b) > 0 && b.conditionHoldStatus === 'authorized' && (
                      <p style={{ margin: '6px 0 0 0' }}>
                        Condition hold: ${getConditionHoldAmount(null, b).toFixed(2)} (owner releases after return).
                      </p>
                    )}
                  </div>
                )}

                {isRentalReviewEligible(b) && !hasUserReviewedRental(myReviews, b.id, user.uid, 'renter-review') && (
                  <button
                    type="button"
                    onClick={() => openReviewModal(b, false)}
                    style={{ marginTop: '12px', padding: '10px 20px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    Review This Gear
                  </button>
                )}

                {isRentalReviewEligible(b) && hasUserReviewedRental(myReviews, b.id, user.uid, 'renter-review') && (
                  <p style={{ marginTop: '12px', color: '#047857', fontWeight: '600' }}>
                    Thanks — your review has been submitted.
                  </p>
                )}
              </div>
            );
            })
          ) : (
            <p style={{ color: '#6b7280', marginBottom: '40px' }}>You haven't rented anything yet.</p>
          )}
          <BookingsPagination
            page={currentRentalsPage}
            totalPages={rentalsTotalPages}
            onPageChange={setRentalsPage}
          />

          <h3 className="bookings-section-title" style={{ color: '#10b981', marginTop: '48px' }}>Scheduled Gear</h3>
          <p className="bookings-section-subtitle">Active rentals on gear you own.</p>
          {sortedScheduledGear.length > 0 ? (
            pagedScheduledGear.map((b) => (
              <div key={b.id} style={{ ...cardStyle, marginBottom: '20px' }}>
                <h4>
                  {b.gearId ? (
                    <Link to={`/gear/${b.gearId}`} style={{ color: '#1f2937', textDecoration: 'none' }}>
                      {b.gearName || 'Gear'}
                    </Link>
                  ) : (
                    b.gearName || 'Gear'
                  )}
                </h4>
                <ProfileRow label="Renter" userId={b.renterId} />
                <p><strong>Dates:</strong> {formatRentalDateRange(b.rentalStartDate, b.rentalEndDate)}</p>
                <p><strong>Status:</strong> {formatRentalStatus(b.status)}</p>
                {isRentalCancelled(b) && (
                  <p style={{ marginTop: '8px', color: '#b45309', fontSize: '0.92rem', lineHeight: '1.5' }}>
                    {b.refundStatus === 'refunded_full'
                      ? 'Cancelled — renter received a full refund.'
                      : b.refundStatus === 'manual_required'
                        ? 'Cancelled — arrange any refund directly with the renter.'
                        : 'This rental was cancelled.'}
                  </p>
                )}
                {b.status === 'approved' && (
                  <p>
                    <strong>Payment:</strong>{' '}
                    <span style={{ color: isRentalPaid(b) ? '#10b981' : '#f59e0b', fontWeight: '600' }}>
                      {isRentalPaid(b)
                        ? 'Paid by renter'
                        : isRentalPaymentScheduled(b)
                          ? 'Renter card on file — charge scheduled'
                          : 'Awaiting renter payment setup'}
                    </span>
                  </p>
                )}
                {b.status === 'approved' && isRentalPaid(b) && (
                  <div style={{ marginTop: '8px', fontSize: '0.92rem', color: '#374151', lineHeight: '1.5' }}>
                    {b.payoutStatus === 'transferred' && getOwnerPayoutSummary(b) && (
                      <p style={{ margin: 0, color: '#047857' }}>
                        ${getOwnerPayoutSummary(b).ownerAmount.toFixed(2)} sent to your connected Stripe account
                        {getOwnerPayoutSummary(b).ownerSharePercent === 100
                          ? ' (100% — no platform fee on this rental).'
                          : ` (${getOwnerPayoutSummary(b).ownerSharePercent}% owner share).`}
                      </p>
                    )}
                    {b.payoutStatus === 'held' && formatPayoutDueLabel(b) && (
                      <p style={{ margin: 0 }}>
                        Your {getOwnerPayoutSummary(b)?.ownerSharePercent ?? OWNER_SHARE_PERCENT}% share ($
                        {getOwnerPayoutSummary(b)?.ownerAmount.toFixed(2) || '—'}) transfers around{' '}
                        {formatPayoutDueLabel(b)} once your Stripe account is connected.
                      </p>
                    )}
                    {b.payoutStatus === 'pending_owner_stripe' && (
                      <p style={{ margin: 0, color: '#b45309' }}>
                        Connect Stripe on your profile to receive your payout.
                      </p>
                    )}
                  </div>
                )}
                <CancelRentalButton
                  rental={{ ...b, viewerId: user.uid }}
                  role="owner"
                  onCancelled={fetchBookings}
                />
                <OwnerReleaseHoldButton
                  rental={{ ...b, viewerId: user.uid }}
                  onReleased={fetchBookings}
                />

                <ConditionReportCard rental={b} userId={user.uid} />

                {b.ownerComment && <p><strong>Your Note:</strong> "{b.ownerComment}"</p>}

                {isRentalReviewEligible(b) && !hasUserReviewedRental(myReviews, b.id, user.uid, 'owner-review') && (
                  <button
                    type="button"
                    onClick={() => openReviewModal(b, true)}
                    style={{ marginTop: '12px', marginRight: '12px', padding: '10px 20px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    Rate Renter
                  </button>
                )}

                {isRentalReviewEligible(b) && hasUserReviewedRental(myReviews, b.id, user.uid, 'owner-review') && (
                  <p style={{ marginTop: '12px', color: '#047857', fontWeight: '600' }}>
                    Renter review submitted.
                  </p>
                )}

                <button 
                  onClick={() => openEditModal(b)}
                  style={{ marginTop: '12px', padding: '10px 20px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Edit Dates
                </button>
              </div>
            ))
          ) : (
            <p style={{ color: '#6b7280' }}>No active rentals on your gear yet.</p>
          )}
          <BookingsPagination
            page={currentScheduledPage}
            totalPages={scheduledTotalPages}
            onPageChange={setScheduledPage}
          />
        </>
      )}

      {/* Edit Rental Modal */}
      {showEditModal && editingRental && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
          <div className="modal-panel" style={{ backgroundColor: 'white', padding: '32px', borderRadius: '16px', width: '90%', maxWidth: '480px' }}>
            <h3>Edit Rental Dates</h3>
            <p><strong>Gear:</strong> {editingRental.gearName || 'Gear'}</p>
            <ProfileRow label="Renter" userId={editingRental.renterId} />

            <label>Start Date</label>
            <input 
              type="date" 
              value={newStartDate} 
              onChange={(e) => setNewStartDate(e.target.value)} 
              style={{ width: '100%', padding: '10px', margin: '8px 0 16px' }}
            />

            <label>End Date</label>
            <input 
              type="date" 
              value={newEndDate} 
              onChange={(e) => setNewEndDate(e.target.value)} 
              style={{ width: '100%', padding: '10px', margin: '8px 0 24px' }}
            />

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button 
                onClick={handleUpdateRentalDates}
                style={{ flex: 1, padding: '12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              >
                Save Changes
              </button>
              <button 
                onClick={() => setShowEditModal(false)}
                style={{ flex: 1, padding: '12px', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        rental={reviewRental}
        user={user}
        isOwner={reviewAsOwner}
        existingReviews={myReviews}
        onSubmitted={fetchBookings}
      />
    </div>
    </>
  );
}
// ==================== SCHEDULED GEAR (OWNER OVERVIEW) ====================
function ScheduledGear({ user }) {
  const [gearWithRentals, setGearWithRentals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchScheduledGear = async () => {
      try {
        // Get all gear owned by user
        const gearQuery = query(collection(db, 'gear'), where('ownerId', '==', user.uid));
        const gearSnap = await getDocs(gearQuery);

        const rentalsQuery = query(
          collection(db, 'rentals'),
          where('ownerId', '==', user.uid),
          where('status', '==', 'approved')
        );
        const rentalsSnap = await getDocs(rentalsQuery);
        const rentalsByGearId = {};

        rentalsSnap.docs.forEach((rentalDoc) => {
          const rental = {
            id: rentalDoc.id,
            ...rentalDoc.data(),
            renterName: rentalDoc.data().renterName || rentalDoc.data().renterId,
          };
          if (!rentalsByGearId[rental.gearId]) {
            rentalsByGearId[rental.gearId] = [];
          }
          rentalsByGearId[rental.gearId].push(rental);
        });

        const results = gearSnap.docs.map((gearDoc) => ({
          ...gearDoc.data(),
          id: gearDoc.id,
          rentals: rentalsByGearId[gearDoc.id] || [],
        }));

        setGearWithRentals(results);
      } catch (error) {
        console.error('Error fetching scheduled gear:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchScheduledGear();
  }, [user]);

  if (!user) {
    return <p style={{ textAlign: 'center', padding: '80px' }}>Please log in to view scheduled gear.</p>;
  }

  return (
    <div style={{ maxWidth: '1100px', margin: '40px auto', padding: '0 24px' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '40px' }}>Scheduled Gear</h2>

      {loading ? (
        <p style={{ textAlign: 'center' }}>Loading your scheduled rentals...</p>
      ) : gearWithRentals.length > 0 ? (
        gearWithRentals.map((gear) => (
          <div key={gear.id} style={{ ...cardStyle, marginBottom: '32px' }}>
            <h3 style={{ marginBottom: '16px' }}>{gear.name}</h3>
            
            {gear.rentals && gear.rentals.length > 0 ? (
              gear.rentals.map((rental) => {
                const days = calculateRentalDays(rental.rentalStartDate, rental.rentalEndDate);
                const total = (gear.pricePerDay || 0) * days;
                return (
                  <div key={rental.id} style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '10px', marginBottom: '12px' }}>
                    <ProfileRow label="Renter" userId={rental.renterId} />
                    <p><strong>Dates:</strong> {formatRentalDateRange(rental.rentalStartDate, rental.rentalEndDate)}</p>
                    <p><strong>Total Cost:</strong> ${total}</p>
                    {rental.ownerComment && <p><strong>Your Note:</strong> "{rental.ownerComment}"</p>}
                  </div>
                );
              })
            ) : (
              <p style={{ color: '#6b7280' }}>No scheduled rentals for this item yet.</p>
            )}
          </div>
        ))
      ) : (
        <p style={{ textAlign: 'center', color: '#6b7280' }}>You have no gear with scheduled rentals.</p>
      )}
    </div>
  );
}
function HomePage({ browseMode = false, user, searchResults, otherItems, onSearchResults, categories, sectionTexts, canEditSite, isContentLoading, onTextChange, onOpenLogin }) {
  const location = useLocation();
  const [hasSearched, setHasSearched] = useState(false);
  const [featuredGear, setFeaturedGear] = useState([]);
  const [wantedPosts, setWantedPosts] = useState([]);
  const [nearbyWanted, setNearbyWanted] = useState([]);
  const [inventoryCount, setInventoryCount] = useState(0);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [wantedLoading, setWantedLoading] = useState(true);
  const [communityStats, setCommunityStats] = useState({
    completedRentals: 0,
    wantedCount: 0,
    expertCount: 0,
  });
  const [categoryPreset, setCategoryPreset] = useState('');
  const welcomeContent = sectionTexts.home || DEFAULT_DESCRIPTION;

  const reloadWantedPosts = useCallback(async () => {
    setWantedLoading(true);
    try {
      const snapshot = await getDocs(
        query(
          collection(db, 'gearWanted'),
          where('status', '==', 'open'),
          orderBy('createdAt', 'desc'),
          limit(24)
        )
      );
      const posts = snapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .filter(isWantedPostActive);
      setWantedPosts(posts);
    } catch (error) {
      console.error('Error loading gear wanted posts:', error);
      setWantedPosts([]);
    } finally {
      setWantedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (browseMode) return undefined;

    const hashTarget = location.hash === '#how-it-works'
      ? 'how-it-works'
      : location.hash === '#gear-wanted'
        ? 'gear-wanted'
        : null;

    if (!hashTarget) return undefined;

    const timer = window.setTimeout(() => {
      document.getElementById(hashTarget)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [browseMode, location.hash, location.pathname]);

  useEffect(() => {
    const loadFeaturedGear = async () => {
      setFeaturedLoading(true);
      try {
        const snapshot = await getDocs(collection(db, 'gear'));
        const allGear = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        setInventoryCount(allGear.length);
        const featured = allGear
          .filter((item) => item.name && getGearPrimaryImage(item))
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        const summaries = await fetchReviewSummariesForGear(featured.map((item) => item.id));
        setFeaturedGear(attachReviewSummaries(featured, summaries));
      } catch (error) {
        console.error('Error loading featured gear:', error);
        setFeaturedGear([]);
        setInventoryCount(0);
      } finally {
        setFeaturedLoading(false);
      }
    };

    const loadCommunityStats = async () => {
      const stats = await fetchCommunityStats();
      setCommunityStats(stats);
    };

    loadFeaturedGear();
    reloadWantedPosts();
    loadCommunityStats();
  }, [reloadWantedPosts]);

  const handleSearchComplete = async (gear, location, proximity, keyword) => {
    setHasSearched(true);
    onSearchResults(gear, location, proximity, keyword);

    const trimmedLocation = location?.trim();
    if (!trimmedLocation || proximity === 'Nationwide') {
      setNearbyWanted([]);
      return;
    }

    try {
      const coords = await geocodeLocation(trimmedLocation);
      if (!coords) {
        setNearbyWanted([]);
        return;
      }

      const wantedSnapshot = await getDocs(
        query(
          collection(db, 'gearWanted'),
          where('status', '==', 'open'),
          orderBy('createdAt', 'desc'),
          limit(48)
        )
      );
      const openPosts = wantedSnapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .filter(isWantedPostActive);
      setNearbyWanted(filterWantedByProximity(openPosts, coords.lat, coords.lon));
    } catch (error) {
      console.error('Error matching gear wanted posts:', error);
      setNearbyWanted([]);
    }
  };

  const handleCategoryBrowse = (categoryId) => {
    setCategoryPreset(categoryId);
    document.getElementById('gear-search')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCategoryPresetApplied = useCallback(() => {
    setCategoryPreset('');
  }, []);

  const pageTitle = browseMode ? 'Browse Gear' : 'Home';
  const pagePath = browseMode ? '/browse' : '/';
  const pageDescription = browseMode
    ? 'Search outdoor gear by category, location, and dates. Find kayaks, bikes, camping equipment, and more from local owners.'
    : DEFAULT_DESCRIPTION;

  return (
    <>
      <PageMeta
        title={pageTitle}
        description={pageDescription}
        path={pagePath}
        jsonLd={browseMode ? undefined : buildHomePageSchema()}
      />
    <div className={`home-page${browseMode ? ' browse-page' : ''}`}>
      {browseMode ? (
        <section className="browse-page-header" aria-label="Browse gear">
          <h1>Browse gear</h1>
          <p>
            Search by category, keyword, location, and dates — or explore what owners have listed below.
          </p>
          {inventoryCount > 0 && (
            <p className="browse-page-inventory">
              {inventoryCount} {inventoryCount === 1 ? 'item' : 'items'} currently on the platform
            </p>
          )}
        </section>
      ) : (
        <HomeHero inventoryCount={inventoryCount} user={user} />
      )}

      {!browseMode && <FoundingListerBanner user={user} />}

      <GearSearch
        onSearchResults={handleSearchComplete}
        categories={categories}
        categoryPreset={categoryPreset}
        onCategoryPresetApplied={handleCategoryPresetApplied}
        compactTitle={!browseMode}
      />

      <GearWantedPostForm
        user={user}
        categories={categories}
        onPosted={reloadWantedPosts}
        onLogin={onOpenLogin}
      />

      {!browseMode && <TrustStrip />}

      {!browseMode && (
        <CommunityPulse
          inventoryCount={inventoryCount}
          wantedCount={wantedPosts.filter(isWantedPostActive).length}
          completedRentals={communityStats.completedRentals}
          expertCount={communityStats.expertCount}
        />
      )}

      <PopularCategories categories={categories} onSelectCategory={handleCategoryBrowse} />

      {!browseMode && <BenefitTiles />}

      {!browseMode && <LocalExpertStrip user={user} />}

      {!browseMode && (
        <div className="home-welcome-section">
          <EditablePageContent
            content={welcomeContent}
            isAdmin={canEditSite}
            isLoading={isContentLoading}
            onSave={(text) => onTextChange('home', text)}
          />
        </div>
      )}

      {!browseMode && <HowItWorks />}

      {!hasSearched && (
        <HomeDiscoverySection
          gear={featuredGear}
          wantedPosts={wantedPosts}
          categories={categories}
          user={user}
          loadingGear={featuredLoading}
          loadingWanted={wantedLoading}
          onLogin={onOpenLogin}
        />
      )}

      {hasSearched && (
        <>
          <section className="home-search-results" aria-label="Search results">
            <h2 className="home-search-results-title">Search results</h2>
            {searchResults.length > 0 ? (
              searchResults.map((gear) => (
                <GearItem key={gear.id} gear={gear} user={user} categories={categories} />
              ))
            ) : (
              <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '1.05rem' }}>
                <p style={{ margin: '0 0 16px' }}>
                  No gear found. Try adjusting your search, browse all listings, or post what you need.
                </p>
                <div className="search-empty-actions">
                  <Link to="/browse" className="search-empty-browse-link">
                    Browse all listings
                  </Link>
                </div>
                <SearchEmptyWantedPrompt />
              </div>
            )}
          </section>
          <GearWantedResultsSection
            posts={nearbyWanted}
            categories={categories}
            user={user}
            onLogin={onOpenLogin}
          />
        </>
      )}

      {hasSearched && otherItems.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <h3 style={{ marginBottom: '24px', color: '#1f2937' }}>Other items in the area</h3>
          {otherItems.map((gear) => (
            <GearItem key={gear.id} gear={gear} user={user} categories={categories} />
          ))}
        </div>
      )}

      {!browseMode && <OwnerListCTA user={user} />}
    </div>
    </>
  );
}


function formatGearLocationInput(location) {
  if (!location) return '';
  if (typeof location === 'string') return formatLocationDisplay(location);
  if (typeof location === 'object') {
    const city = location.city || '';
    const state = location.state || '';
    const zip = location.zip || '';
    const parts = [city, state].filter(Boolean).join(', ');
    return formatLocationDisplay(zip ? `${parts}${parts ? ' ' : ''}${zip}` : parts);
  }
  return '';
}

// ==================== YOUR HANGAR (FIXED - CLICKABLE CARDS + WORKING EDIT) ====================
function YourHangar({ user, categories }) {
  const { showToast } = useToast();
  const { needsStripeConnect } = useStripeConnectBanner(user);
  const [gearItems, setGearItems] = useState([]);
  const [newGear, setNewGear] = useState({ 
    name: '', 
    category: '', 
    description: '', 
    pricePerDay: '', 
    location: '', 
    dateRanges: [{ startDate: '', endDate: '' }] 
  });
  const [editingGear, setEditingGear] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedGearHistory, setSelectedGearHistory] = useState(null);
  const [vehicleDisclaimerAcknowledged, setVehicleDisclaimerAcknowledged] = useState(false);

  const requiresVehicleDisclaimer = isVehicleDisclaimerCategory(newGear.category, categories);
  // Blocking the submit button, rather than rejecting the submit afterwards,
  // keeps a half-filled form intact while the owner reads the notice.
  const blockedByVehicleDisclaimer = requiresVehicleDisclaimer && !vehicleDisclaimerAcknowledged;

  // Listings always display "City, ST", whatever the owner typed. The lookup
  // runs when they leave the field; when a zip or name matches more than one
  // real city they are asked which one rather than the site guessing.
  const [locationLookup, setLocationLookup] = useState(null);
  const [resolvedLocation, setResolvedLocation] = useState(null);
  const [locationChecking, setLocationChecking] = useState(false);
  const [savingGear, setSavingGear] = useState(false);

  const applyResolvedLocation = (candidate) => {
    setResolvedLocation(candidate);
    setLocationLookup(null);
    setNewGear((prev) => ({ ...prev, location: candidate.label }));
  };

  const runLocationLookup = async (rawText) => {
    const text = String(rawText || '').trim();
    if (!text) {
      setResolvedLocation(null);
      setLocationLookup(null);
      return;
    }
    if (resolvedLocation && resolvedLocation.label === text) return;

    setLocationChecking(true);
    try {
      const result = await resolveLocationInput(text);
      if (result.status === 'resolved') {
        applyResolvedLocation(result.candidates[0]);
      } else {
        setResolvedLocation(null);
        setLocationLookup(result);
      }
    } catch (error) {
      setResolvedLocation(null);
      setLocationLookup({ status: 'unavailable', candidates: [], query: text });
    } finally {
      setLocationChecking(false);
    }
  };

  const locationTyped = typeof newGear.location === 'string' ? newGear.location.trim() : '';
  // A failed lookup must not strand a listing, so an unreachable service lets
  // the typed text through; a genuinely unmatched place does not.
  const locationServiceDown = locationLookup?.status === 'unavailable';
  const blockedByLocation =
    Boolean(locationTyped) && !resolvedLocation && !locationServiceDown;
  const selectedCategoryName = getCategoryDisplayName(newGear.category, categories);

  const handleCategoryChange = (categoryId) => {
    setNewGear({ ...newGear, category: categoryId });
    setVehicleDisclaimerAcknowledged(false);
  };

  const validateVehicleDisclaimer = () => {
    if (!requiresVehicleDisclaimer) return true;
    if (vehicleDisclaimerAcknowledged) return true;
    showToast('Please read and acknowledge the vehicle rental notice before continuing.', 'error');
    return false;
  };

  const getVehicleDisclaimerFields = () => {
    if (!requiresVehicleDisclaimer) {
      return {
        vehicleDisclaimerAcknowledgedAt: null,
        vehicleDisclaimerVersion: null,
      };
    }
    return {
      vehicleDisclaimerAcknowledgedAt: new Date().toISOString(),
      vehicleDisclaimerVersion: VEHICLE_DISCLAIMER_VERSION,
    };
  };

  const refreshGear = useCallback(async () => {
    if (!user) return;
    try {
      const gearQuery = query(collection(db, 'gear'), where('ownerId', '==', user.uid));
      const gearSnapshot = await getDocs(gearQuery);
      setGearItems(gearSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error refreshing gear:', error);
    }
  }, [user]);

  useEffect(() => {
    refreshGear();
  }, [refreshGear]);

  // Date range helpers
  const handleAddDateRange = () => {
    if (newGear.dateRanges.length < 5) {
      setNewGear({ ...newGear, dateRanges: [...newGear.dateRanges, { startDate: '', endDate: '' }] });
    } else showToast('Maximum 5 date ranges allowed.', 'error');
  };

  const handleRemoveDateRange = (index) => {
    if (newGear.dateRanges.length > 1) {
      setNewGear({ ...newGear, dateRanges: newGear.dateRanges.filter((_, i) => i !== index) });
    }
  };

  const handleDateRangeChange = (index, field, value) => {
    const updated = [...newGear.dateRanges];
    updated[index][field] = value;
    setNewGear({ ...newGear, dateRanges: updated });
  };

  const handleFileChange = (e, field) => {
    if (field === 'mainPicture') setNewGear({ ...newGear, mainPicture: e.target.files[0] });
    else if (field === 'additionalPictures') setNewGear({ ...newGear, additionalPictures: Array.from(e.target.files) });
  };

  const uploadImage = async (file, path) => {
    // Shrink before upload so listing photos go through on weak mobile signal.
    const prepared = await compressImage(file);
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, prepared);
    return await getDownloadURL(storageRef);
  };

  const handleAddGear = async (e) => {
    e.preventDefault();
    const descriptionError = getGearDescriptionValidationError(newGear.description);
    if (descriptionError) {
      showToast(descriptionError, 'error');
      return;
    }
    if (!validateVehicleDisclaimer()) return;

    setSavingGear(true);
    try {
      let coordinates = { latitude: null, longitude: null };
      // The city chooser already geocoded the chosen place; only fall back to a
      // fresh lookup when it could not (service down, so we kept typed text).
      const locationText = (resolvedLocation?.label || formatGearLocationInput(newGear.location)).trim();
      if (Number.isFinite(resolvedLocation?.latitude) && Number.isFinite(resolvedLocation?.longitude)) {
        coordinates = { latitude: resolvedLocation.latitude, longitude: resolvedLocation.longitude };
      } else {
        try {
          const geoRes = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(locationText)}&format=json&limit=1`);
          const geoData = await geoRes.json();
          if (geoData.length > 0) {
            coordinates.latitude = parseFloat(geoData[0].lat);
            coordinates.longitude = parseFloat(geoData[0].lon);
          }
        } catch (geoError) {
          console.warn('Could not geocode location:', geoError);
        }
      }

      let mainPictureUrl = '';
      if (newGear.mainPicture) {
        const path = `gear/${user.uid}/${Date.now()}_${newGear.mainPicture.name}`;
        mainPictureUrl = await uploadImage(newGear.mainPicture, path);
      }

      const { mainPicture, additionalPictures, ...gearFields } = newGear;
      const gearData = {
        ...gearFields,
        location: formatLocationDisplay(locationText),
        locationCity: resolvedLocation?.city || null,
        locationState: resolvedLocation?.state || null,
        locationZip: resolvedLocation?.zip || null,
        coordinates,
        ownerId: user.uid,
        createdAt: new Date().toISOString(),
        mainPicture: mainPictureUrl || null,
        additionalPictures: [],
        conditionGuaranteeAmount: capConditionGuaranteeAmount(newGear.conditionGuaranteeAmount),
        ...getVehicleDisclaimerFields(),
      };

      await addDoc(collection(db, 'gear'), gearData);
      await refreshGear();
      setVehicleDisclaimerAcknowledged(false);
      setResolvedLocation(null);
      setLocationLookup(null);
      setNewGear({ category: '', name: '', location: '', pricePerDay: '', conditionGuaranteeAmount: '', dateRanges: [{ startDate: '', endDate: '' }], willingToShip: false, description: '', mainPicture: null, additionalPictures: [], ownerDescription: '' });

      let foundingNote = '';
      try {
        // Allow the founding-lister Cloud Function a moment to run, then check status.
        await new Promise((resolve) => setTimeout(resolve, 1200));
        const { fetchMyPlatformFeeStatus } = await import('./platformFees');
        const feeStatus = await fetchMyPlatformFeeStatus();
        if (feeStatus?.ownerExempt && feeStatus?.ownerExemptReason === 'founding_lister') {
          foundingNote =
            ' You are a founding lister — you keep 100% of rentals for life (we cover platform + card fees).';
        } else if (
          typeof feeStatus?.foundingSlotsRemaining === 'number' &&
          feeStatus.foundingSlotsRemaining > 0
        ) {
          foundingNote = ` Founding free-for-life slots left: ${feeStatus.foundingSlotsRemaining}.`;
        }
      } catch (_) {
        // Non-blocking
      }

      showToast(`Gear added successfully!${foundingNote}`, 'success');
    } catch (error) {
      showToast('Error adding gear: ' + error.message, 'error');
    } finally {
      setSavingGear(false);
    }
  };

    const handleEditGear = (gear) => {
    setEditingGear(gear);
    // If this listing already carries an acknowledgement, keep it rather than
    // making the owner re-check the box on every edit.
    setVehicleDisclaimerAcknowledged(Boolean(gear.vehicleDisclaimerAcknowledgedAt));
    // Treat an already-canonical "City, ST" as settled; anything older (a bare
    // zip, say) stays unresolved so it gets corrected on this edit.
    setLocationLookup(null);
    const storedLabel = formatGearLocationInput(gear.location);
    const storedParts = parseTypedLocation(storedLabel);
    setResolvedLocation(
      storedParts.cityText && storedParts.stateText
        ? {
            city: storedParts.cityText,
            state: storedParts.stateText,
            label: formatCityState({ city: storedParts.cityText, state: storedParts.stateText }),
            latitude: gear.coordinates?.latitude ?? null,
            longitude: gear.coordinates?.longitude ?? null,
            zip: gear.locationZip || null,
          }
        : null
    );
    setNewGear({
      name: gear.name || '',
      category: gear.category || '',
      description: gear.description || '',
      pricePerDay: gear.pricePerDay || '',
      location: formatGearLocationInput(gear.location),
      dateRanges: gear.dateRanges || [{ startDate: '', endDate: '' }],
      conditionGuaranteeAmount: gear.conditionGuaranteeAmount || '',
      willingToShip: gear.willingToShip || false,
      ownerDescription: gear.ownerDescription || '',
      mainPicture: null,
      additionalPictures: []
    });
  };

  const handleUpdateGear = async (e) => {
    e.preventDefault();
    const descriptionError = getGearDescriptionValidationError(newGear.description);
    if (descriptionError) {
      showToast(descriptionError, 'error');
      return;
    }
    if (!validateVehicleDisclaimer()) return;

    setSavingGear(true);
    try {
      let mainPictureUrl = editingGear.mainPicture;
      if (newGear.mainPicture) {
        mainPictureUrl = await uploadImage(newGear.mainPicture, `gear/${user.uid}/${Date.now()}_${newGear.mainPicture.name}`);
      }
      const locationText = (resolvedLocation?.label || formatGearLocationInput(newGear.location)).trim();
      const { mainPicture, additionalPictures, ...gearFields } = newGear;
      // Keep whatever coordinates we have: the chosen city's, else the ones
      // already stored. Blanking them silently removed the listing from
      // distance-based search.
      const nextCoordinates =
        Number.isFinite(resolvedLocation?.latitude) && Number.isFinite(resolvedLocation?.longitude)
          ? { latitude: resolvedLocation.latitude, longitude: resolvedLocation.longitude }
          : {
              latitude: editingGear.coordinates?.latitude ?? null,
              longitude: editingGear.coordinates?.longitude ?? null,
            };
      const gearData = {
        ...gearFields,
        location: formatLocationDisplay(locationText),
        locationCity: resolvedLocation?.city || editingGear.locationCity || null,
        locationState: resolvedLocation?.state || editingGear.locationState || null,
        locationZip: resolvedLocation?.zip || editingGear.locationZip || null,
        coordinates: nextCoordinates,
        mainPicture: mainPictureUrl,
        ownerId: user.uid,
        conditionGuaranteeAmount: capConditionGuaranteeAmount(newGear.conditionGuaranteeAmount),
        ...getVehicleDisclaimerFields(),
      };
      await updateDoc(doc(db, 'gear', editingGear.id), gearData);
      await refreshGear();
      setEditingGear(null);
      setVehicleDisclaimerAcknowledged(false);
      setResolvedLocation(null);
      setLocationLookup(null);
      showToast('Gear updated successfully!', 'success');
    } catch (error) {
      showToast('Error updating gear: ' + error.message, 'error');
    } finally {
      setSavingGear(false);
    }
  };

  const handleDeleteGear = async (gearId) => {
    if (!window.confirm('Delete this gear?')) return;
    try {
      await deleteDoc(doc(db, 'gear', gearId));
      await refreshGear();
      showToast('Gear deleted successfully!', 'success');
    } catch (error) {
      showToast('Error deleting gear: ' + error.message, 'error');
    }
  };

  

    const openRentalHistory = async (gear) => {
    try {
      const rentalsQuery = query(
        collection(db, 'rentals'),
        where('gearId', '==', gear.id),
        where('ownerId', '==', user.uid)
      );
      const rentalsSnap = await getDocs(rentalsQuery);
      const history = rentalsSnap.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      })).sort((a, b) => new Date(b.rentalStartDate) - new Date(a.rentalStartDate)); // newest first

      setSelectedGearHistory({ ...gear, history });
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error loading history:', error);
      showToast('Could not load rental history.', 'error');
    }
  };

  // Early return must be INSIDE the function
  if (!user) {
    return <p style={{ textAlign: 'center', padding: '80px' }}>Please log in to access Your Hangar.</p>;
  }

return (
    <>
      <PageMeta
        title="Your Hangar"
        description="List and manage your outdoor gear for rent on ATTACK! Your Adventure."
        path="/your-hangar"
      />
    <div style={{ maxWidth: '1100px', margin: '40px auto', padding: '0 24px' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '16px' }}>Your Hangar</h2>
      <p className="hangar-lead">
        List gear so someone else can get outside this weekend. Share what it is best for, nearby spots, and any tips you
        would give at pickup.
      </p>
      <FoundingListerBanner user={user} compact />
      {needsStripeConnect && <StripeConnectBanner />}
      <MyGearWantedSection user={user} categories={categories} />
      <HangarListingChecklist
        gearDraft={{
          ...newGear,
          location: formatGearLocationInput(newGear.location),
          hasMainPicture: Boolean(editingGear?.mainPicture),
        }}
        gearCount={gearItems.length}
        editing={Boolean(editingGear)}
      />
      {/* Add/Edit Form */}
      <div style={cardStyle}>
        <h3>{editingGear ? 'Edit Gear' : 'Add New Gear'}</h3>
        <form onSubmit={editingGear ? handleUpdateGear : handleAddGear} className="hangar-form" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
  <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '16px' }}>
    <select
      value={newGear.category}
      onChange={(e) => handleCategoryChange(e.target.value)}
      required
      style={{ padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1.05rem' }}
    >
      <option value="">Select Category</option>
      {categories.length > 0 ? (
        categories.map(cat => (
          <option key={cat.id} value={cat.id}>{cat.name}</option>
        ))
      ) : (
        <option disabled>No categories available</option>
      )}
    </select>

    {requiresVehicleDisclaimer && (
      <VehicleCategoryDisclaimerBanner
        categoryName={selectedCategoryName}
        acknowledged={vehicleDisclaimerAcknowledged}
        onAcknowledgedChange={setVehicleDisclaimerAcknowledged}
      />
    )}
  </div>

  {/* Left Column */}
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    <input
      type="text"
      placeholder="Item Name"
      value={newGear.name}
      onChange={(e) => setNewGear({ ...newGear, name: e.target.value })}
      required
      style={{ padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1.05rem' }}
    />

    <input
      type="text"
      placeholder="Location (city, state, or zip code)"
      value={typeof newGear.location === 'string' ? newGear.location : formatGearLocationInput(newGear.location)}
      onChange={(e) => setNewGear({ ...newGear, location: e.target.value })}
      /* Resolve to "City, ST" only once the field is left — doing any
         reformatting per keystroke made spaces impossible to type. */
      onBlur={(e) => runLocationLookup(e.target.value)}
      required
      style={{ padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '1.05rem' }}
    />

    {locationChecking && (
      <p style={{ margin: '-8px 0 0', color: '#6b7280', fontSize: '0.9rem' }}>Looking up that location…</p>
    )}

    {!locationChecking && resolvedLocation && (
      <p style={{ margin: '-8px 0 0', color: '#047857', fontSize: '0.9rem' }}>
        Will display as <strong>{resolvedLocation.label}</strong>.
      </p>
    )}

    {!locationChecking && locationLookup?.status === 'ambiguous' && (
      <div style={{ margin: '-8px 0 0', padding: '14px', borderRadius: '10px', backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
        <p style={{ margin: '0 0 10px', color: '#92400e', fontSize: '0.92rem', fontWeight: 600 }}>
          {locationLookup.zip
            ? `Zip ${locationLookup.zip} covers more than one city. Which one is this gear in?`
            : `More than one city is called "${locationLookup.query}". Which one is this gear in?`}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {locationLookup.candidates.map((candidate) => (
            <button
              key={candidate.label}
              type="button"
              onClick={() => applyResolvedLocation(candidate)}
              style={{ padding: '8px 14px', borderRadius: '999px', border: '1px solid #d97706', backgroundColor: '#fff', color: '#92400e', fontWeight: 600, cursor: 'pointer' }}
            >
              {candidate.label}
            </button>
          ))}
        </div>
      </div>
    )}

    {!locationChecking && locationLookup?.status === 'not_found' && (
      <p style={{ margin: '-8px 0 0', color: '#b45309', fontSize: '0.9rem' }}>
        We could not match that to a city. Try a 5-digit zip code, or “City, ST”.
      </p>
    )}

    {!locationChecking && locationServiceDown && (
      <p style={{ margin: '-8px 0 0', color: '#b45309', fontSize: '0.9rem' }}>
        The location lookup is unavailable right now, so we will save exactly what you typed.
      </p>
    )}

    <div style={{ display: 'flex', gap: '8px' }}>
      <span style={{ padding: '14px 12px', backgroundColor: '#f3f4f6', borderRadius: '10px 0 0 10px', border: '1px solid #d1d5db' }}>$</span>
      <input
        type="number"
        placeholder="Price per Day"
        value={newGear.pricePerDay}
        onChange={(e) => setNewGear({ ...newGear, pricePerDay: e.target.value })}
        required
        style={{ flex: 1, padding: '14px', borderRadius: '0 10px 10px 0', border: '1px solid #d1d5db', fontSize: '1.05rem' }}
      />
    </div>

    <div style={{ display: 'flex', gap: '8px' }}>
      <span style={{ padding: '14px 12px', backgroundColor: '#f3f4f6', borderRadius: '10px 0 0 10px', border: '1px solid #d1d5db' }}>$</span>
      <input
        type="number"
        placeholder="Condition Guarantee Amount"
        value={newGear.conditionGuaranteeAmount}
        onChange={(e) => {
          const nextValue = e.target.value;
          setNewGear({
            ...newGear,
            conditionGuaranteeAmount:
              nextValue !== '' && Number(nextValue) > MAX_CONDITION_GUARANTEE
                ? String(MAX_CONDITION_GUARANTEE)
                : nextValue,
          });
        }}
        min="0"
        max={MAX_CONDITION_GUARANTEE}
        required
        style={{ flex: 1, padding: '14px', borderRadius: '0 10px 10px 0', border: '1px solid #d1d5db', fontSize: '1.05rem' }}
      />
    </div>
    <p style={{ margin: '-8px 0 0 0', color: '#6b7280', fontSize: '0.85rem' }}>
      Maximum ${MAX_CONDITION_GUARANTEE.toLocaleString()}. Held as a pending authorization on the renter&apos;s card until the owner releases it after return.
    </p>
    {requiresVehicleDisclaimer && (
      <p className="vehicle-disclaimer-condition-hint">
        For {selectedCategoryName || 'this category'}, set this amount to your insurance deductible.
      </p>
    )}

    {/* Date Ranges */}
    <div>
      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Available Dates</label>
      {newGear.dateRanges.map((range, index) => (
        <div key={index} style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <input
            type="date"
            value={range.startDate}
            onChange={(e) => handleDateRangeChange(index, 'startDate', e.target.value)}
            required
            style={{ flex: 1, padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db' }}
          />
          <input
            type="date"
            value={range.endDate}
            onChange={(e) => handleDateRangeChange(index, 'endDate', e.target.value)}
            required
            style={{ flex: 1, padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db' }}
          />
          {newGear.dateRanges.length > 1 && (
            <button type="button" onClick={() => handleRemoveDateRange(index)} style={{ padding: '10px 16px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '8px' }}>
              Remove
            </button>
          )}
        </div>
      ))}
      {newGear.dateRanges.length < 5 && (
        <button type="button" onClick={handleAddDateRange} style={{ padding: '10px 20px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px' }}>
          Add Date Range
        </button>
      )}
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <input
        type="checkbox"
        checked={newGear.willingToShip}
        onChange={(e) => setNewGear({ ...newGear, willingToShip: e.target.checked })}
      />
      <label>Willing to Ship</label>
    </div>

    <div>
      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
        Description (unique, at least {MIN_GEAR_DESCRIPTION_LENGTH} characters)
      </label>
      <p style={{ margin: '0 0 10px', color: '#6b7280', fontSize: '0.92rem', lineHeight: 1.5 }}>
        Help renters choose your gear. Mention condition, what is included, pickup details, and what adventures it is best for.
      </p>
      <textarea
        placeholder="Example: Well-maintained 12-ft touring kayak with paddle, life vest, and dry bag. Great for calm lakes and slow rivers around Denver."
        value={newGear.description}
        onChange={(e) => setNewGear({ ...newGear, description: e.target.value })}
        required
        minLength={MIN_GEAR_DESCRIPTION_LENGTH}
        style={{ width: '100%', padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db', minHeight: '120px', resize: 'vertical', boxSizing: 'border-box' }}
      />
      <p style={{ margin: '8px 0 0', color: '#9ca3af', fontSize: '0.85rem' }}>
        {newGear.description.trim().length}/{MIN_GEAR_DESCRIPTION_LENGTH} characters minimum
      </p>
    </div>

    <div>
      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
        Best for & local tips (optional)
      </label>
      <p style={{ margin: '0 0 10px', color: '#6b7280', fontSize: '0.92rem', lineHeight: 1.5 }}>
        Where you use this gear, nearby spots, or a tip you would share at pickup — that is what turns a listing into an
        experience.
      </p>
      <textarea
        placeholder="Example: Great for a calm lake day near Denver. Happy to share my favorite put-in when you pick up."
        value={newGear.ownerDescription}
        onChange={(e) => setNewGear({ ...newGear, ownerDescription: e.target.value })}
        rows={3}
        maxLength={320}
        style={{ width: '100%', padding: '14px', borderRadius: '10px', border: '1px solid #d1d5db', resize: 'vertical', boxSizing: 'border-box' }}
      />
    </div>

    <div>
      <label style={{ display: 'block', marginBottom: '8px' }}>Main Picture</label>
      <input
        type="file"
        accept="image/*,.heic,.heif"
        onChange={(e) => handleFileChange(e, 'mainPicture')}
        required={!editingGear}
        style={{ width: '100%' }}
      />
    </div>

    <div>
      <label style={{ display: 'block', marginBottom: '8px' }}>Additional Pictures</label>
      <input
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        onChange={(e) => handleFileChange(e, 'additionalPictures')}
        style={{ width: '100%' }}
      />
    </div>
  </div>

  

  {/* Form Buttons */}
  <div className="form-buttons" style={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '16px' }}>
    {blockedByLocation && !blockedByVehicleDisclaimer && (
      <p style={{ flexBasis: '100%', margin: 0, color: '#b45309', fontWeight: 600, fontSize: '0.95rem' }}>
        Pick the city for this listing above so it displays as “City, ST”. Nothing you have typed will be lost.
      </p>
    )}
    {blockedByVehicleDisclaimer && (
      <p style={{ gridColumn: '1 / -1', flexBasis: '100%', margin: 0, color: '#b45309', fontWeight: 600, fontSize: '0.95rem' }}>
        Tick the acknowledgement in the notice above to {editingGear ? 'save your changes' : 'add this listing'}. Nothing you have typed will be lost.
      </p>
    )}
    <button 
      type="submit" 
      disabled={blockedByVehicleDisclaimer || blockedByLocation || savingGear}
      style={{ flex: 1, padding: '16px', backgroundColor: (blockedByVehicleDisclaimer || blockedByLocation || savingGear) ? '#9ca3af' : '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', fontSize: '1.05rem', cursor: (blockedByVehicleDisclaimer || blockedByLocation || savingGear) ? 'not-allowed' : 'pointer' }}
    >
      {savingGear
        ? 'Saving… iPhone photos take a few seconds to convert'
        : editingGear ? 'Update Gear' : 'Add Gear'}
    </button>
    {editingGear && (
      <button 
        type="button" 
        onClick={() => setEditingGear(null)}
        style={{ flex: 1, padding: '16px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '10px', fontWeight: '600' }}
      >
        Cancel Edit
      </button>
    )}
  </div>
</form>
      </div>
      
            {/* Gear Listings - Clean Cards with Clickable Name */}
      <div className="hangar-gear-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '28px' }}>
        {gearItems.map((gear) => {
          const mainImage = gear.mainPicture || gear.images?.[0] || null;

          return (
            <div key={gear.id} style={{ 
              ...cardStyle, 
              position: 'relative', 
              padding: '20px',
              overflow: 'hidden'
            }}>
              {mainImage && (
                <img 
                  src={mainImage} 
                  alt={gear.name}
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '10px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
              )}

              {/* FIXED CLICKABLE LINK - matches the route */}
              <Link 
                to={`/gear/${gear.id}`} 
                style={{ 
                  textDecoration: 'none', 
                  color: '#1f2937', 
                  fontSize: '1.35rem', 
                  fontWeight: '600',
                  display: 'block',
                  marginBottom: '12px'
                }}
              >
                {gear.name}
              </Link>

              <p><strong>Category:</strong> {gear.category}</p>
              <p><strong>Price/day:</strong> ${gear.pricePerDay}</p>
              <p><strong>Location:</strong> {formatGearLocationInput(gear.location) || 'N/A'}</p>
              <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <ProfileChip userId={gear.ownerId || user.uid} label="Owner" />
              </div>

              <div style={{ marginTop: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button onClick={() => handleEditGear(gear)} style={{ backgroundColor: '#3b82f6', color: 'white', padding: '10px 18px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem' }}>
                  Edit
                </button>
                <button onClick={() => openRentalHistory(gear)} style={{ backgroundColor: '#10b981', color: 'white', padding: '10px 18px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem' }}>
                  View Rental History
                </button>
                <button onClick={() => handleDeleteGear(gear.id)} style={{ backgroundColor: '#ef4444', color: 'white', padding: '10px 18px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem' }}>
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
           {/* Improved Rental History Modal */}
      {showHistoryModal && selectedGearHistory && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '32px', borderRadius: '16px', maxWidth: '720px', width: '92%', maxHeight: '88vh', overflowY: 'auto' }}>
            <h2 style={{ marginBottom: '16px' }}>{selectedGearHistory.name} — Rental History</h2>
            <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ color: '#6b7280', fontSize: '0.95rem' }}>Owner:</span>
              <ProfileChip userId={selectedGearHistory.ownerId || user.uid} />
            </div>

            {selectedGearHistory.history && selectedGearHistory.history.length > 0 ? (
              selectedGearHistory.history.map((rental) => {
                const statusColor = rental.status === 'approved' ? '#10b981' : rental.status === 'denied' ? '#ef4444' : '#f59e0b';

                return (
                  <div key={rental.id} style={{ padding: '18px', backgroundColor: '#f9fafb', borderRadius: '12px', marginBottom: '16px' }}>
                    <p><strong>Status:</strong> <span style={{ color: statusColor, fontWeight: '600' }}>{formatRentalStatus(rental.status)}</span></p>
                    <div style={{ margin: '10px 0', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <ProfileChip userId={rental.renterId} label="Renter" />
                      <MessageUserButton
                        user={user}
                        recipientId={rental.renterId}
                        gearId={selectedGearHistory.id}
                        gearName={selectedGearHistory.name}
                        label="Message Renter"
                        initialMessage={`Hi! Following up about your rental of ${selectedGearHistory.name}.`}
                        compact
                      />
                    </div>
                    <p><strong>Dates:</strong> {formatRentalDateRange(rental.rentalStartDate, rental.rentalEndDate)}</p>
                    {rental.ownerComment && <p><strong>Note:</strong> "{rental.ownerComment}"</p>}
                  </div>
                );
              })
            ) : (
              <p>No rental history for this item yet.</p>
            )}

            <button 
              onClick={() => setShowHistoryModal(false)} 
              style={{ marginTop: '28px', backgroundColor: '#6b7280', color: 'white', padding: '12px 28px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}


function omitUndefinedFields(data) {
  return Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined)
  );
}

// ==================== MESSAGING ====================
function Messaging({ user, unreadConversationIds = new Set() }) {
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sending, setSending] = useState(false);
  const profileCacheRef = useRef({});
  const messagesEndRef = useRef(null);
  const activeConversationIdRef = useRef(null);
  const activeConversationRef = useRef(null);
  const latestMessageDocsRef = useRef([]);

  useEffect(() => {
    activeConversationIdRef.current = activeConversation?.id || null;
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'messages'),
      where('participants', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      latestMessageDocsRef.current = snapshot.docs;
      const currentActive = activeConversationRef.current;

      if (currentActive) {
        setMessages(getThreadMessages(snapshot.docs, currentActive));
      }

      try {
        const convList = buildConversationList(snapshot.docs, user.uid);
        const enriched = await Promise.all(
          convList.map(async (conv) => {
            if (!profileCacheRef.current[conv.otherUserId]) {
              const profile = await fetchUserProfile(conv.otherUserId);
              profileCacheRef.current[conv.otherUserId] = getPublicName(profile, conv.otherUserName || 'Member');
            }

            return {
              ...conv,
              otherUserName: profileCacheRef.current[conv.otherUserId],
            };
          })
        );

        enriched.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
        setConversations(enriched);

        if (currentActive) {
          const resolvedActive = findActiveConversationInList(enriched, currentActive);
          if (resolvedActive) {
            activeConversationIdRef.current = resolvedActive.id;
            activeConversationRef.current = resolvedActive;
            setActiveConversation(resolvedActive);
            setMessages(getThreadMessages(snapshot.docs, resolvedActive));
          }
        }
      } catch (error) {
        console.error('Error loading conversations:', error);
      } finally {
        setLoading(false);
      }
    }, (error) => {
      console.error('Conversation listener error:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const engageConversation = useCallback(async (conv) => {
    if (!user?.uid || !conv?.id) return;
    try {
      await Promise.all([
        markConversationEngaged(user.uid, conv.id),
        touchMessagingEngagement(user.uid),
      ]);
    } catch (error) {
      console.error('Failed to update conversation engagement:', error);
    }
  }, [user?.uid]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!activeConversation?.id || !user?.uid || messages.length === 0) return;
    const latestMessage = messages[messages.length - 1];
    if (!latestMessage || latestMessage.senderId === user.uid) return;
    engageConversation(activeConversation);
  }, [messages, activeConversation, user?.uid, engageConversation]);

  const loadMessages = useCallback((conv) => {
    activeConversationIdRef.current = conv.id;
    activeConversationRef.current = conv;
    setActiveConversation(conv);
    setMessages(getThreadMessages(latestMessageDocsRef.current, conv));
    engageConversation(conv);
  }, [engageConversation]);

  useEffect(() => {
    const openConversationId = location.state?.openConversationId;
    const pendingConversation = location.state?.pendingConversation;
    if (!openConversationId || activeConversation?.id === openConversationId) return;

    const targetConversation =
      conversations.find((conv) => conv.id === openConversationId) || pendingConversation;

    if (!targetConversation) return;
    if (loading && !pendingConversation) return;

    loadMessages(targetConversation);
    navigate('/messaging', { replace: true, state: {} });
  }, [location.state, conversations, loading, activeConversation, navigate, loadMessages]);

  const deleteConversation = async (conv) => {
    const deleteLabel = conv.conversationType === 'local-expert'
      ? 'this local area guide chat'
      : `"${conv.gearName}"`;
    if (!window.confirm(`Delete conversation about ${deleteLabel}?`)) return;

    setConversations(prev => prev.filter(c => c.id !== conv.id));

    if (activeConversation && activeConversation.id === conv.id) {
      activeConversationIdRef.current = null;
      activeConversationRef.current = null;
      setActiveConversation(null);
      setMessages([]);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConversation || sending) return;

    const messageData = omitUndefinedFields({
      conversationId: activeConversation.id,
      senderId: user.uid,
      recipientId: activeConversation.otherUserId,
      gearId: activeConversation.gearId,
      conversationType: activeConversation.conversationType,
      gearName: activeConversation.gearName,
      text: newMessage.trim(),
      timestamp: new Date().toISOString(),
      participants: [user.uid, activeConversation.otherUserId],
    });

    setSending(true);
    try {
      await addDoc(collection(db, 'messages'), messageData);
      setNewMessage('');
      await engageConversation(activeConversation);
    } catch (error) {
      showToast('Failed to send message: ' + error.message, 'error');
    } finally {
      setSending(false);
    }
  };

  // Filter conversations
  const filteredConversations = conversations.filter(conv =>
    conv.gearName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.otherUserName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!user) {
    return <p style={{ textAlign: 'center', padding: '80px' }}>Please log in to view messages.</p>;
  }

  const closeMobileChat = () => {
    activeConversationIdRef.current = null;
    activeConversationRef.current = null;
    setActiveConversation(null);
    setMessages([]);
  };

  return (
    <div
      className={`messaging-layout${activeConversation ? ' messaging-chat-active' : ''}`}
      style={{ maxWidth: '1280px', margin: '40px auto', padding: '0 24px', display: 'flex', gap: '24px', height: 'calc(100vh - 180px)' }}
    >
      {/* Sidebar */}
      <div className="messaging-sidebar" style={{ width: '300px', borderRight: '1px solid #e5e7eb', paddingRight: '20px', overflowY: 'auto' }}>
        <h2 style={{ marginBottom: '16px' }}>Messages</h2>

        <input
          type="text"
          placeholder="Search conversations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '95%',
            padding: '12px 16px',
            borderRadius: '9999px',
            border: '1px solid #d1d5db',
            marginBottom: '24px',
            fontSize: '1rem'
          }}
        />

        {loading ? (
          <p>Loading...</p>
        ) : filteredConversations.length > 0 ? (
          filteredConversations.map((conv) => {
            const isUnread = unreadConversationIds.has(conv.id);
            return (
            <div
              key={conv.id}
              onClick={() => loadMessages(conv)}
              className={isUnread ? 'messaging-conversation-unread' : ''}
              style={{
                ...cardStyle,
                marginBottom: '12px',
                cursor: 'pointer',
                backgroundColor: activeConversation?.id === conv.id
                  ? '#f0f9ff'
                  : isUnread
                    ? '#eff6ff'
                    : '#ffffff',
                border: isUnread ? '1px solid #93c5fd' : cardStyle.border,
                position: 'relative'
              }}
            >
              <div style={{ fontWeight: isUnread ? '700' : '600', paddingRight: '30px' }}>
                {conv.conversationType === 'local-expert' ? (
                  <span style={{ color: '#047857' }}>🗺️ {conv.gearName}</span>
                ) : conv.conversationType === 'gear-wanted-alert' ? (
                  <span style={{ color: '#c2410c' }}>{conv.gearName}</span>
                ) : conv.gearId ? (
                  <Link
                    to={`/gear/${conv.gearId}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: '#1f2937', textDecoration: 'none' }}
                  >
                    {conv.gearName}
                  </Link>
                ) : (
                  conv.gearName
                )}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span>With</span>
                <ProfileLink userId={conv.otherUserId} size="sm" />
              </div>
              {conv.lastMessage && (
                <div style={{
                  fontSize: '0.85rem',
                  color: isUnread ? '#1d4ed8' : '#9ca3af',
                  marginTop: '6px',
                  fontWeight: isUnread ? '600' : '400',
                }}>
                  {isUnread && <span style={{ marginRight: '6px' }}>●</span>}
                  {formatDatesInMessageText(conv.lastMessage).substring(0, 55)}...
                </div>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteConversation(conv);
                }}
                style={{
                  position: 'absolute',
                  top: '14px',
                  right: '14px',
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  color: '#ef4444',
                  cursor: 'pointer',
                  lineHeight: 1
                }}
                title="Delete conversation"
              >
                ×
              </button>
            </div>
          );
          })
        ) : (
          <p style={{ color: '#6b7280' }}>No conversations found.</p>
        )}
      </div>

      {/* Chat Area */}
      <div className="messaging-chat-panel" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {activeConversation ? (
          <>
            <button
              type="button"
              className="messaging-back-button mobile-only"
              onClick={closeMobileChat}
            >
              ← Back to conversations
            </button>
            <div style={{ padding: '16px 0', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: '0 0 8px 0' }}>
                {activeConversation.conversationType === 'local-expert' ? (
                  <span style={{ color: '#047857' }}>🗺️ Local Area Guide</span>
                ) : activeConversation.conversationType === 'gear-wanted-alert' ? (
                  <span style={{ color: '#c2410c' }}>Nearby gear wanted</span>
                ) : (
                  <>
                    Chat about{' '}
                    {activeConversation.gearId ? (
                      <Link to={`/gear/${activeConversation.gearId}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>
                        {activeConversation.gearName}
                      </Link>
                    ) : (
                      activeConversation.gearName
                    )}
                  </>
                )}
              </h3>
              {activeConversation.conversationType === 'local-expert' && (
                <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '0.92rem' }}>
                  Ask about trails, activities, and local spots — not tied to a rental.
                </p>
              )}
              {activeConversation.conversationType === 'gear-wanted-alert' && (
                <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '0.92rem' }}>
                  A member near you posted a gear wanted ad. Reply if you can help.
                </p>
              )}
              <div style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ color: '#6b7280' }}>With</span>
                <ProfileLink userId={activeConversation.otherUserId} size="sm" />
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 0', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {messages.map((msg) => {
                const isOwnMessage = msg.senderId === user.uid;
                return (
                  <div
                    key={msg.id}
                    style={{
                      alignSelf: isOwnMessage ? 'flex-end' : 'flex-start',
                      maxWidth: '75%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                    }}
                  >
                    {!isOwnMessage && (
                      <ProfileLink
                        userId={msg.senderId}
                        size="sm"
                        showAvatar
                        style={{ color: '#6b7280', fontSize: '0.8rem' }}
                      />
                    )}
                    <div
                      style={{
                        padding: '14px 18px',
                        borderRadius: isOwnMessage ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        backgroundColor: isOwnMessage ? '#3b82f6' : '#f1f5f9',
                        color: isOwnMessage ? 'white' : '#1f2937',
                      }}
                    >
                      {formatDatesInMessageText(msg.text)}
                      <div style={{ fontSize: '0.75rem', opacity: 0.75, marginTop: '6px', textAlign: 'right' }}>
                        {formatMessageTimestamp(msg.timestamp || msg.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className="messaging-compose-row" style={{ display: 'flex', gap: '12px', paddingTop: '16px' }}>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !sending && sendMessage()}
                placeholder="Type a message..."
                style={{ flex: 1, padding: '14px 20px', borderRadius: '9999px', border: '1px solid #d1d5db' }}
              />
              <button 
                onClick={sendMessage}
                disabled={!newMessage.trim() || sending}
                style={{ padding: '14px 32px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '9999px', fontWeight: '600' }}
              >
                Send
              </button>
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '1.1rem' }}>
            Select a conversation from the left to start chatting
          </div>
        )}
      </div>
    </div>
  );
}

const formInputStyle = {
  width: '100%',
  padding: '16px',
  marginBottom: '20px',
  borderRadius: '10px',
  border: '1px solid #d1d5db',
  fontSize: '1.05rem',
  boxSizing: 'border-box',
};

function getPasswordChecks(password) {
  return {
    length: password.length >= 10,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
}

function isPasswordValid(password) {
  const checks = getPasswordChecks(password);
  return checks.length && checks.uppercase && checks.lowercase && checks.special;
}

function CreateProfile() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [userName, setUserName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  const passwordChecks = getPasswordChecks(password);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const isFormValid =
    userName.trim() &&
    email.trim() &&
    phone.trim() &&
    isPasswordValid(password) &&
    passwordsMatch;

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!isPasswordValid(password)) {
      setFormError('Password does not meet all requirements.');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const { createUserWithEmailAndPassword, updateProfile } = await import('firebase/auth');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await updateProfile(user, { displayName: userName.trim() });
      await Promise.all([
        setDoc(doc(db, 'users', user.uid), {
          userName: userName.trim(),
          publicName: userName.trim(),
          aboutMe: '',
          location: '',
          profilePhotoUrl: null,
          createdAt: new Date().toISOString(),
        }),
        savePrivateProfile(user.uid, {
          email: email.trim(),
          phone: phone.trim(),
        }),
      ]);
      setSuccess(true);
    } catch (error) {
      setFormError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={{ maxWidth: '600px', margin: '80px auto', textAlign: 'center', padding: '40px' }}>
        <h2 style={{ color: '#10b981' }}>Account Created!</h2>
        <p style={{ fontSize: '1.2rem', marginTop: '16px', color: '#374151' }}>
          Welcome to ATTACK! Your Adventure. You can now log in and start exploring gear.
        </p>
        <Link
          to="/"
          style={{
            marginTop: '32px',
            display: 'inline-block',
            padding: '12px 32px',
            backgroundColor: '#3b82f6',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '10px',
            fontWeight: '600',
          }}
        >
          Go to Home
        </Link>
      </div>
    );
  }

  const requirementStyle = (met) => ({
    fontSize: '0.82rem',
    color: met ? '#10b981' : '#6b7280',
    marginBottom: '4px',
  });

  return (
    <div style={{ maxWidth: '560px', margin: '60px auto', padding: '0 24px' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '12px', color: '#1f2937' }}>Create Account</h1>
      <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '40px', fontSize: '1.05rem' }}>
        Join the community and start sharing or renting outdoor gear.
      </p>

      <form
        onSubmit={handleCreateAccount}
        style={{
          backgroundColor: '#ffffff',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 4px 25px rgba(0,0,0,0.08)',
          border: '1px solid #e5e7eb',
        }}
      >
        <input
          type="text"
          placeholder="Full Name"
          value={userName}
          onChange={(e) => setUserName(e.target.value)}
          required
          style={formInputStyle}
        />

        <input
          type="email"
          placeholder="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={formInputStyle}
        />

        <input
          type="tel"
          placeholder="Phone Number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
          style={formInputStyle}
        />

        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          style={{ ...formInputStyle, marginBottom: '8px' }}
        />

        <div style={{ marginBottom: '20px', padding: '0 4px' }}>
          <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 8px 0', fontWeight: '500' }}>
            Password requirements:
          </p>
          <p style={requirementStyle(passwordChecks.length)}>
            {passwordChecks.length ? '✓' : '○'} At least 10 characters
          </p>
          <p style={requirementStyle(passwordChecks.uppercase)}>
            {passwordChecks.uppercase ? '✓' : '○'} One uppercase letter (A–Z)
          </p>
          <p style={requirementStyle(passwordChecks.lowercase)}>
            {passwordChecks.lowercase ? '✓' : '○'} One lowercase letter (a–z)
          </p>
          <p style={requirementStyle(passwordChecks.special)}>
            {passwordChecks.special ? '✓' : '○'} One special symbol (!@#$%…)
          </p>
        </div>

        <input
          type="password"
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          style={{
            ...formInputStyle,
            borderColor:
              confirmPassword.length > 0
                ? passwordsMatch
                  ? '#10b981'
                  : '#ef4444'
                : '#d1d5db',
          }}
        />

        {confirmPassword.length > 0 && !passwordsMatch && (
          <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '-12px', marginBottom: '16px' }}>
            Passwords do not match.
          </p>
        )}

        {formError && (
          <p style={{ color: '#ef4444', fontSize: '0.9rem', marginBottom: '16px' }}>{formError}</p>
        )}

        <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: '0.9rem', lineHeight: 1.6 }}>
          By creating an account, you agree to our{' '}
          <Link to="/terms" style={{ color: '#3b82f6' }}>Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" style={{ color: '#3b82f6' }}>Privacy Policy</Link>.
        </p>

        <button
          type="submit"
          disabled={!isFormValid || isSubmitting}
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: !isFormValid || isSubmitting ? '#9ca3af' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '1.1rem',
            fontWeight: '600',
            cursor: !isFormValid || isSubmitting ? 'not-allowed' : 'pointer',
          }}
        >
          {isSubmitting ? 'Creating Account...' : 'Create Account'}
        </button>

        <p style={{ textAlign: 'center', marginTop: '24px', color: '#6b7280', fontSize: '0.95rem' }}>
          Already have an account?{' '}
          <Link to="/" style={{ color: '#3b82f6', fontWeight: '600' }}>
            Log in from Home
          </Link>
        </p>
      </form>
    </div>
  );
}

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    try {
      const { sendPasswordResetEmail } = await import('firebase/auth');
      await sendPasswordResetEmail(auth, email.trim());
      setSuccess(true);
    } catch (error) {
      setFormError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={{ maxWidth: '600px', margin: '80px auto', textAlign: 'center', padding: '40px' }}>
        <h2 style={{ color: '#10b981' }}>Check Your Email</h2>
        <p style={{ fontSize: '1.1rem', marginTop: '16px', color: '#374151', lineHeight: '1.6' }}>
          If an account exists for <strong>{email}</strong>, a password reset link has been sent.
        </p>
        <Link
          to="/"
          style={{
            marginTop: '32px',
            display: 'inline-block',
            padding: '12px 32px',
            backgroundColor: '#3b82f6',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '10px',
            fontWeight: '600',
          }}
        >
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '60px auto', padding: '0 24px' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '12px', color: '#1f2937' }}>Reset Password</h1>
      <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '40px', fontSize: '1.05rem' }}>
        Enter your email and we&apos;ll send you a link to reset your password.
      </p>

      <form
        onSubmit={handleResetPassword}
        style={{
          backgroundColor: '#ffffff',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 4px 25px rgba(0,0,0,0.08)',
          border: '1px solid #e5e7eb',
        }}
      >
        <input
          type="email"
          placeholder="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={formInputStyle}
        />

        {formError && (
          <p style={{ color: '#ef4444', fontSize: '0.9rem', marginTop: '-8px', marginBottom: '16px' }}>
            {formError}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !email.trim()}
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: isSubmitting || !email.trim() ? '#9ca3af' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '1.1rem',
            fontWeight: '600',
            cursor: isSubmitting || !email.trim() ? 'not-allowed' : 'pointer',
          }}
        >
          {isSubmitting ? 'Sending...' : 'Send Reset Link'}
        </button>

        <p style={{ textAlign: 'center', marginTop: '24px', color: '#6b7280', fontSize: '0.95rem' }}>
          Remember your password?{' '}
          <Link to="/" style={{ color: '#3b82f6', fontWeight: '600' }}>
            Back to Login
          </Link>
        </p>
      </form>
    </div>
  );
}

export default App;